'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { adminApi, fantasyApi, type AdminAccountInfo } from '../api/endpoints'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Input from '../components/ui/Input'
import Modal from '../components/ui/Modal'
import AdminHorsePanel from '../components/admin/AdminHorsePanel'
import AdminCoinRatesPanel from '../components/admin/AdminCoinRatesPanel'
import { formatNumber, formatDate, getCoinIcon, getCoinName } from '../utils/helpers'
import {
  RewardCode,
  GiftPrize,
  AirdropLeaderboardEntry,
  AdminStats,
  User,
  GameRecord,
  FootballMatch,
  FootballClub,
  FootballLeague,
  FantasyPlayer,
  PlayerGroup,
  CoinTransfer,
  AdminMessage,
  GuessNumberStatus,
  FootballGameMode,
  RaffleWinner,
  MatchPlayerStat,
  PlayerPosition,
  SupportTicket,
  AdminUserDetail,
  CoinMap,
} from '../types'

type AdminTab = 
  | 'dashboard' 
  | 'codes'
  | 'gifts'
  | 'scores' 
  | 'monthly' 
  | 'users' 
  | 'guess-number'
  | 'morse'
  | 'football' 
  | 'fantasy' 
  | 'horse'
  | 'transfers' 
  | 'coin-rates'
  | 'tickets'
  | 'messages'
  | 'backup'
  | 'admins'

const TABS: { id: AdminTab; label: string; icon: string }[] = [
  { id: 'dashboard', label: 'داشبورد', icon: '📊' },
  { id: 'codes', label: 'کدهای پاداش', icon: '🎫' },
  { id: 'gifts', label: 'جوایز هدیه', icon: '🎁' },
  { id: 'scores', label: 'امتیازهای روزانه', icon: '📈' },
  { id: 'monthly', label: 'امتیازهای ماهانه', icon: '📊' },
  { id: 'users', label: 'کاربران', icon: '👥' },
  { id: 'guess-number', label: 'حدس عدد', icon: '🎯' },
  { id: 'morse', label: 'کد مورس', icon: '📡' },
  { id: 'football', label: 'فوتبال', icon: '⚽' },
  { id: 'fantasy', label: 'فانتزی', icon: '🏆' },
  { id: 'horse', label: 'اسب', icon: '🐎' },
  { id: 'transfers', label: 'تراکنش‌ها', icon: '💸' },
  { id: 'coin-rates', label: 'نرخ سکه', icon: '💱' },
  { id: 'tickets', label: 'چت پشتیبانی', icon: '🎧' },
  { id: 'messages', label: 'پیام‌ها', icon: '💬' },
  { id: 'backup', label: 'بکاپ', icon: '💾' },
  { id: 'admins', label: 'ادمین‌ها', icon: '🛡️' },
]

const ASSIGNABLE_PERMS = TABS.filter((t) => t.id !== 'admins').map((t) => t.id)

export default function AdminPage() {
  const [token, setToken] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [authenticated, setAuthenticated] = useState(false)
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [backupDownloading, setBackupDownloading] = useState(false)
  const [backupMessage, setBackupMessage] = useState<string | null>(null)
  const [currentAdmin, setCurrentAdmin] = useState<AdminAccountInfo | null>(null)
  const [permissionLabels, setPermissionLabels] = useState<Record<string, string>>({})
  const [managedAdmins, setManagedAdmins] = useState<AdminAccountInfo[]>([])
  const [adminForm, setAdminForm] = useState({ username: '', password: '', permissions: [] as string[] })
  const [editingAdmin, setEditingAdmin] = useState<AdminAccountInfo | null>(null)
  const [savingAdmin, setSavingAdmin] = useState(false)

  const [codes, setCodes] = useState<RewardCode[]>([])
  const [giftPrizes, setGiftPrizes] = useState<GiftPrize[]>([])
  const [giftWinners, setGiftWinners] = useState<RewardCode[]>([])
  const [giftForm, setGiftForm] = useState({ title: '', description: '', image_url: '', sort_order: 0 })
  const [editingGift, setEditingGift] = useState<GiftPrize | null>(null)
  const [giftPhotoUploading, setGiftPhotoUploading] = useState(false)
  const [scores, setScores] = useState<AirdropLeaderboardEntry[]>([])
  const [monthlyScores, setMonthlyScores] = useState<AirdropLeaderboardEntry[]>([])
  const [stats, setStats] = useState<AdminStats | null>(null)
  
  const [users, setUsers] = useState<User[]>([])
  const [usersTotal, setUsersTotal] = useState(0)
  const [usersPage, setUsersPage] = useState(0)
  const [usersSearch, setUsersSearch] = useState('')
  const [selectedUserDetail, setSelectedUserDetail] = useState<AdminUserDetail | null>(null)
  const [userDetailLoading, setUserDetailLoading] = useState(false)
  const [historyFilter, setHistoryFilter] = useState<'all' | 'coin' | 'score' | 'gift' | 'transfer' | 'activity'>('all')
  
  const [guessWinners, setGuessWinners] = useState<GameRecord[]>([])
  const [guessStatus, setGuessStatus] = useState<GuessNumberStatus | null>(null)
  const [generatingGuessNumber, setGeneratingGuessNumber] = useState(false)
  const [morseStatus, setMorseStatus] = useState<{
    date: string
    words: string[]
    status: string
    solves_today: number
    window_open: boolean
  } | null>(null)
  const [morseWordsInput, setMorseWordsInput] = useState(['', '', '', '', ''])
  

  const [matches, setMatches] = useState<FootballMatch[]>([])
  const [matchesPage, setMatchesPage] = useState(0)
  const [leagues, setLeagues] = useState<FootballLeague[]>([])
  const [editingLeague, setEditingLeague] = useState<FootballLeague | null>(null)
  const [leagueForm, setLeagueForm] = useState({ name: '', description: '', start_date: '', end_date: '', is_active: true })
  const [savingLeague, setSavingLeague] = useState(false)
  const [creatingLeague, setCreatingLeague] = useState(false)
  const [footballMode, setFootballMode] = useState<FootballGameMode>('league')
  const [matchStatusFilter, setMatchStatusFilter] = useState<'all' | 'pending' | 'started' | 'finished' | 'cancelled'>('pending')
  const [newMatch, setNewMatch] = useState({
    home_team: '',
    away_team: '',
    home_team_id: 0,
    away_team_id: 0,
    start_time: '',
    mode: 'league' as FootballGameMode,
  })
  const [showMatchModal, setShowMatchModal] = useState(false)
  const [clubs, setClubs] = useState<FootballClub[]>([])
  const [showClubModal, setShowClubModal] = useState(false)
  const [editingClub, setEditingClub] = useState<FootballClub | null>(null)
  const [clubForm, setClubForm] = useState({ name: '', flag_url: '' })
  const [clubFlagPreview, setClubFlagPreview] = useState<string | null>(null)
  const [clubFlagUploading, setClubFlagUploading] = useState(false)
  const [showInlineClub, setShowInlineClub] = useState<'home' | 'away' | null>(null)
  const [showResultModal, setShowResultModal] = useState(false)
  const [resultMatchId, setResultMatchId] = useState<number | null>(null)
  const [resultHomeTeam, setResultHomeTeam] = useState('')
  const [resultAwayTeam, setResultAwayTeam] = useState('')
  const [resultHomeScore, setResultHomeScore] = useState('')
  const [resultAwayScore, setResultAwayScore] = useState('')
  const [resultRaffleCount, setResultRaffleCount] = useState('5')
  const [allGroups, setAllGroups] = useState<PlayerGroup[]>([])
  const [showMatchGroupsModal, setShowMatchGroupsModal] = useState(false)
  const [matchGroupsMatchId, setMatchGroupsMatchId] = useState<number | null>(null)
  const [matchGroupsSelected, setMatchGroupsSelected] = useState<number[]>([])
  const [savingMatchGroups, setSavingMatchGroups] = useState(false)
  
  const [fantasyPlayers, setFantasyPlayers] = useState<FantasyPlayer[]>([])
  const [newPlayer, setNewPlayer] = useState({ name: '', position: 'fwd', team: '', photo_url: '' })
  const [showPlayerModal, setShowPlayerModal] = useState(false)
  const [editingPlayer, setEditingPlayer] = useState<FantasyPlayer | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [photoUploading, setPhotoUploading] = useState(false)
  
  const [fantasyGroups, setFantasyGroups] = useState<PlayerGroup[]>([])
  const [groupsPage, setGroupsPage] = useState(0)
  const [groupsTotal, setGroupsTotal] = useState(0)
  const [newGroup, setNewGroup] = useState({ title: '', max_cards: '' })
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState<PlayerGroup | null>(null)
  const [groupPlayers, setGroupPlayers] = useState<FantasyPlayer[]>([])
  const [showGroupPlayersModal, setShowGroupPlayersModal] = useState(false)
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  
  const [matchWinners, setMatchWinners] = useState<RaffleWinner[]>([])
  const [winnersMatchId, setWinnersMatchId] = useState<number | null>(null)
  const [statsMatchId, setStatsMatchId] = useState<number | null>(null)
  const [matchPlayers, setMatchPlayers] = useState<FantasyPlayer[]>([])
  const [playerStats, setPlayerStats] = useState<Record<number, Partial<MatchPlayerStat>>>({})
  const [savingStats, setSavingStats] = useState(false)
  const [transfers, setTransfers] = useState<CoinTransfer[]>([])
  const [transfersPage, setTransfersPage] = useState(0)
  const [transfersFilter, setTransfersFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  
  const [messages, setMessages] = useState<AdminMessage[]>([])
  const [messagesPage, setMessagesPage] = useState(0)
  const [newMessage, setNewMessage] = useState({ user_id: '', text: '' })
  const [showMessageModal, setShowMessageModal] = useState(false)

  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null)
  const [ticketReply, setTicketReply] = useState('')
  const [ticketBusy, setTicketBusy] = useState(false)

  const [showCodeModal, setShowCodeModal] = useState(false)
  const [newCode, setNewCode] = useState({ user_id: '', award_type: 'daily' as 'daily' | 'monthly', valid_hours: 0, code: '' })

  const today = new Date().toISOString().split('T')[0]

  const fetchData = useCallback(async () => {
    if (!authenticated) return
    setLoading(true)
    setError(null)
    try {
      switch (activeTab) {
        case 'dashboard': {
          const res = await adminApi.getDashboardStats()
          setStats(res.stats)
          break
        }
        case 'codes': {
          const res = await adminApi.getRewardCodes()
          setCodes(res.codes)
          break
        }
        case 'gifts': {
          const [prizesRes, winnersRes] = await Promise.all([
            adminApi.getGiftPrizes(),
            adminApi.getGiftWinners(),
          ])
          setGiftPrizes(prizesRes.prizes || [])
          setGiftWinners(winnersRes.winners || [])
          break
        }
        case 'scores': {
          const res = await adminApi.getDailyScores(today)
          setScores(res.scores)
          break
        }
        case 'monthly': {
          const res = await adminApi.getMonthlyScores(today)
          setMonthlyScores(res.scores)
          break
        }
        case 'users': {
          const res = await adminApi.getUsers(20, usersPage * 20)
          setUsers(res.users)
          setUsersTotal(res.total)
          break
        }
        case 'guess-number': {
          const [winnersRes, statusRes] = await Promise.all([
            adminApi.getAllGuessWinners(100),
            adminApi.getGuessNumberStatus(),
          ])
          setGuessWinners(winnersRes.winners)
          setGuessStatus(statusRes)
          break
        }
        case 'morse': {
          const statusRes = await adminApi.getMorseAdminStatus()
          setMorseStatus(statusRes)
          if (statusRes.words?.length === 5) {
            setMorseWordsInput(statusRes.words)
          }
          break
        }
        case 'football': {
          const [res, groupsRes, leaguesRes, clubsRes] = await Promise.all([
            adminApi.getAllMatches(20, matchesPage * 20, footballMode),
            adminApi.getPlayerGroups(200, 0),
            adminApi.getLeagues(50, 0),
            adminApi.getFootballClubs(),
          ])
          setMatches(res.matches)
          setAllGroups(groupsRes.groups)
          setLeagues(leaguesRes.leagues)
          setClubs(clubsRes.clubs || [])
          break
        }
        case 'fantasy': {
          const [playersRes, groupsRes] = await Promise.all([
            adminApi.getFantasyPlayers(),
            adminApi.getPlayerGroups(20, groupsPage * 20),
          ])
          setFantasyPlayers(playersRes.players)
          setFantasyGroups(groupsRes.groups)
          break
        }
        case 'horse':
        case 'backup':
        case 'coin-rates':
          break
        case 'admins': {
          const res = await adminApi.listAdmins()
          setManagedAdmins(res.admins || [])
          setPermissionLabels(res.permission_labels || {})
          break
        }
        case 'transfers': {
          if (transfersFilter === 'pending') {
            const res = await adminApi.getPendingTransfers(20, transfersPage * 20)
            setTransfers(res.transfers)
          } else {
            const res = await adminApi.getAllTransfers(20, transfersPage * 20)
            setTransfers(res.transfers)
          }
          break
        }
        case 'tickets': {
          const res = await adminApi.listSupportTickets(undefined, 50, 0)
          setTickets(res.tickets || [])
          break
        }
        case 'messages': {
          const res = await adminApi.getMessages(20, messagesPage * 20)
          setMessages(res.messages)
          break
        }
      }
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout()
        return
      }
      setError(err.response?.data?.message || 'خطا در بارگذاری داده‌ها')
    } finally {
      setLoading(false)
    }
  }, [authenticated, activeTab, usersPage, matchesPage, groupsPage, transfersPage, messagesPage, transfersFilter, today, footballMode])

  const fetchMatchWinners = useCallback(async (matchId: number) => {
    setWinnersMatchId(matchId)
    try {
      const res = await adminApi.getLeagueRaffleWinners(matchId)
      setMatchWinners(res.winners || [])
    } catch {
      setMatchWinners([])
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Keep the open support chat fresh while admin is reading/replying.
  useEffect(() => {
    if (activeTab !== 'tickets' || !activeTicket?.id) return
    const id = activeTicket.id
    const timer = window.setInterval(async () => {
      try {
        const res = await adminApi.getSupportTicket(id)
        setActiveTicket(res.ticket)
      } catch {
        /* ignore transient poll errors */
      }
    }, 5000)
    return () => window.clearInterval(timer)
  }, [activeTab, activeTicket?.id])

  const applyAdminSession = (admin: AdminAccountInfo, labels?: Record<string, string>) => {
    setCurrentAdmin(admin)
    if (labels) setPermissionLabels(labels)
    const allowed = new Set(admin.is_super ? TABS.map((t) => t.id) : admin.permissions)
    if (admin.is_super) allowed.add('admins')
    const first = TABS.find((t) => allowed.has(t.id))
    setActiveTab((prev) => (allowed.has(prev) ? prev : (first?.id || 'dashboard')))
  }

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) return
    setError(null)
    try {
      const res = await adminApi.login(username.trim(), password.trim())
      localStorage.setItem('admin_token', res.token)
      setToken(res.token)
      setAuthenticated(true)
      setUsername('')
      setPassword('')
      if (res.admin) {
        applyAdminSession(res.admin)
      } else {
        const me = await adminApi.getMe()
        applyAdminSession(me.admin, me.permission_labels)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'نام کاربری یا پسورد نامعتبر')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    setAuthenticated(false)
    setToken('')
    setCurrentAdmin(null)
    setManagedAdmins([])
    setEditingAdmin(null)
    setCodes([])
    setScores([])
    setMonthlyScores([])
    setStats(null)
    setUsers([])
    setGuessWinners([])
    setGuessStatus(null)
    setMatches([])
    setFantasyPlayers([])
    setFantasyGroups([])
    setGroupPlayers([])
    setTransfers([])
    setMessages([])
  }

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token')
    if (!savedToken) return
    setToken(savedToken)
    setAuthenticated(true)
    void (async () => {
      try {
        const me = await adminApi.getMe()
        applyAdminSession(me.admin, me.permission_labels)
      } catch {
        handleLogout()
      }
    })()
  }, [])

  const canAccess = useCallback((tab: AdminTab) => {
    if (!currentAdmin) return false
    if (currentAdmin.is_super) return true
    if (tab === 'admins') return false
    return currentAdmin.permissions.includes(tab)
  }, [currentAdmin])

  const visibleTabs = useMemo(() => TABS.filter((t) => canAccess(t.id)), [canAccess])

  if (!authenticated) {
    return (
      <div className="mini-app-container pb-20 min-h-screen">
        <div className="p-4 pt-16">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => window.history.back()} className="text-gray-400 hover:text-white">
              →
            </button>
            <span className="text-2xl">🔐</span>
            <h1 className="text-xl font-bold">ورود ادمین</h1>
          </div>

          <Card>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  نام کاربری
                </label>
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleLogin()
                  }}
                  placeholder="admin"
                  dir="ltr"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl focus:outline-none focus:border-primary text-center"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  پسورد
                </label>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleLogin()
                  }}
                  placeholder="admin123456"
                  dir="ltr"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl focus:outline-none focus:border-primary text-center"
                />
              </div>

              <Button
                variant="primary"
                onClick={() => void handleLogin()}
                disabled={!username.trim() || !password.trim()}
              >
                ورود
              </Button>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-900/30 text-red-300 rounded-lg text-sm text-center">
                {error}
              </div>
            )}
          </Card>
        </div>
      </div>
    )
  }

  const handleCreateCode = async () => {
    setError(null)
    try {
      const res = await adminApi.createRewardCode({
        user_id: newCode.user_id ? parseInt(newCode.user_id) : undefined,
        award_type: newCode.award_type,
        valid_hours: newCode.valid_hours,
        code: newCode.code || undefined,
      })
      setShowCodeModal(false)
      setNewCode({ user_id: '', award_type: 'daily', valid_hours: 0, code: '' })
      if (activeTab === 'codes') fetchData()
      if (activeTab === 'dashboard') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ایجاد کد')
    }
  }

  const emptyMatchForm = (mode: FootballGameMode = footballMode) => ({
    home_team: '',
    away_team: '',
    home_team_id: 0,
    away_team_id: 0,
    start_time: defaultKickoff(),
    mode,
  })

  const handleCreateMatch = async () => {
    setError(null)
    if (newMatch.start_time && new Date(newMatch.start_time).getTime() <= Date.now()) {
      setError('زمان شروع باید در آینده باشد، وگرنه مسابقه بلافاصله از لیست کاربران حذف می‌شود')
      return
    }
    const homeClub = clubs.find((c) => c.id === newMatch.home_team_id)
    const awayClub = clubs.find((c) => c.id === newMatch.away_team_id)
    const homeName = homeClub?.name || newMatch.home_team.trim()
    const awayName = awayClub?.name || newMatch.away_team.trim()
    if (!homeName || !awayName) {
      setError('تیم میزبان و مهمان را انتخاب یا وارد کن')
      return
    }
    try {
      await adminApi.createFootballMatch({
        home_team: homeName,
        away_team: awayName,
        home_team_id: newMatch.home_team_id || undefined,
        away_team_id: newMatch.away_team_id || undefined,
        start_time: Math.floor(new Date(newMatch.start_time).getTime() / 1000),
        mode: footballMode,
      })
      setShowMatchModal(false)
      setShowInlineClub(null)
      setNewMatch(emptyMatchForm(footballMode))
      if (activeTab === 'football') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ایجاد مسابقه')
    }
  }

  const openClubModal = (club?: FootballClub) => {
    if (club) {
      setEditingClub(club)
      setClubForm({ name: club.name, flag_url: club.flag_url || '' })
      setClubFlagPreview(club.flag_url || null)
    } else {
      setEditingClub(null)
      setClubForm({ name: '', flag_url: '' })
      setClubFlagPreview(null)
    }
    setShowClubModal(true)
  }

  const handleClubFlagUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setClubFlagUploading(true)
    setError(null)
    try {
      const res = await adminApi.uploadPlayerPhoto(file)
      setClubForm((prev) => ({ ...prev, flag_url: res.url }))
      setClubFlagPreview(res.url)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در آپلود پرچم')
    } finally {
      setClubFlagUploading(false)
    }
  }

  const handleSaveClub = async () => {
    setError(null)
    if (!clubForm.name.trim()) {
      setError('نام باشگاه الزامی است')
      return
    }
    try {
      if (editingClub) {
        await adminApi.updateFootballClub(editingClub.id, {
          name: clubForm.name.trim(),
          flag_url: clubForm.flag_url,
        })
      } else {
        const res = await adminApi.createFootballClub({
          name: clubForm.name.trim(),
          flag_url: clubForm.flag_url || undefined,
        })
        if (showInlineClub === 'home') {
          setNewMatch((prev) => ({ ...prev, home_team_id: res.club.id, home_team: res.club.name }))
        } else if (showInlineClub === 'away') {
          setNewMatch((prev) => ({ ...prev, away_team_id: res.club.id, away_team: res.club.name }))
        }
      }
      setShowClubModal(false)
      setShowInlineClub(null)
      setEditingClub(null)
      setClubForm({ name: '', flag_url: '' })
      setClubFlagPreview(null)
      const clubsRes = await adminApi.getFootballClubs()
      setClubs(clubsRes.clubs || [])
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره باشگاه')
    }
  }

  const handleDeleteClub = async (club: FootballClub) => {
    if (!confirm(`باشگاه «${club.name}» حذف شود؟`)) return
    setError(null)
    try {
      await adminApi.deleteFootballClub(club.id)
      setClubs((prev) => prev.filter((c) => c.id !== club.id))
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف باشگاه')
    }
  }

  const activeClubs = clubs.filter((c) => c.is_active)

  const handleProcessLeagueResult = async (matchId: number, homeScore: number, awayScore: number) => {
    setError(null)
    try {
      await adminApi.processLeagueMatchResult(matchId, homeScore, awayScore)
      if (activeTab === 'football') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ثبت نتیجه')
    }
  }

  const handleProcessDreamTeams = async (matchId: number) => {
    setError(null)
    try {
      await adminApi.processDreamTeamResults(matchId)
      if (activeTab === 'football') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در پردازش تیم‌های رویایی')
    }
  }

  const handleOpenPlayerStats = async (matchId: number) => {
    setError(null)
    try {
      const [poolRes, statsRes] = await Promise.all([
        fantasyApi.getMatchPlayers(matchId),
        adminApi.getMatchPlayerStats(matchId),
      ])
      setMatchPlayers(poolRes.players)
      const prefilled: Record<number, Partial<MatchPlayerStat>> = {}
      statsRes.stats.forEach((s) => {
        prefilled[s.player_id] = s
      })
      setPlayerStats(prefilled)
      setStatsMatchId(matchId)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دریافت بازیکنان مسابقه')
    }
  }

  const handleSavePlayerStats = async () => {
    if (statsMatchId === null) return
    setSavingStats(true)
    setError(null)
    try {
      const stats: MatchPlayerStat[] = matchPlayers.map((p) => ({
        id: 0,
        match_id: statsMatchId,
        player_id: p.id,
        position: p.position,
        goals: playerStats[p.id]?.goals || 0,
        assists: playerStats[p.id]?.assists || 0,
        yellow_cards: playerStats[p.id]?.yellow_cards || 0,
        clean_sheet: playerStats[p.id]?.clean_sheet || false,
        played_full_time: playerStats[p.id]?.played_full_time ?? true,
      }))
      await adminApi.setMatchPlayerStats(statsMatchId, stats)
      setStatsMatchId(null)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره آمار بازیکنان')
    } finally {
      setSavingStats(false)
    }
  }

  const handleOpenLeague = (league: FootballLeague | null) => {
    setError(null)
    if (!league) {
      const today = new Date()
      const nextYear = new Date()
      nextYear.setFullYear(today.getFullYear() + 1)
      setLeagueForm({
        name: '',
        description: '',
        start_date: today.toISOString().slice(0, 10),
        end_date: nextYear.toISOString().slice(0, 10),
        is_active: true,
      })
      setEditingLeague(null)
      return
    }
    setEditingLeague(league)
    setLeagueForm({
      name: league.name,
      description: league.description || '',
      start_date: league.start_date ? league.start_date.slice(0, 10) : '',
      end_date: league.end_date ? league.end_date.slice(0, 10) : '',
      is_active: league.is_active,
    })
  }

  const handleSaveLeague = async () => {
    if (!editingLeague) return
    if (!leagueForm.name.trim()) {
      setError('نام لیگ را وارد کنید')
      return
    }
    setSavingLeague(true)
    setError(null)
    try {
      await adminApi.updateLeague(editingLeague!.id, {
        name: leagueForm.name.trim(),
        description: leagueForm.description.trim(),
        is_active: leagueForm.is_active,
        ...(leagueForm.start_date ? { start_date: Math.floor(new Date(leagueForm.start_date).getTime() / 1000) } : {}),
        ...(leagueForm.end_date ? { end_date: Math.floor(new Date(leagueForm.end_date).getTime() / 1000) } : {}),
      })
      setEditingLeague(null)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره لیگ')
    } finally {
      setSavingLeague(false)
    }
  }

  const handleToggleLeagueActive = async (league: FootballLeague) => {
    setError(null)
    try {
      await adminApi.updateLeague(league.id, { name: league.name, is_active: !league.is_active })
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در تغییر وضعیت لیگ')
    }
  }

  const handleEnsureActiveLeague = async () => {
    setError(null)
    setCreatingLeague(true)
    try {
      const forMode = leagues.filter((l) => l.mode === footballMode)
      const inactive = forMode.find((l) => !l.is_active)
      if (inactive) {
        await adminApi.updateLeague(inactive.id, { name: inactive.name, is_active: true })
      } else {
        const now = Math.floor(Date.now() / 1000)
        await adminApi.createLeague({
          name: footballMode === 'big_match' ? 'بازی بزرگ' : 'لیگ روزانه',
          mode: footballMode,
          description: footballMode === 'big_match' ? 'لیگ هفتگی بازی بزرگ' : 'لیگ روزانه پیش‌بینی',
          start_date: now,
          end_date: now + 365 * 24 * 60 * 60,
        })
      }
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در آماده‌سازی لیگ')
    } finally {
      setCreatingLeague(false)
    }
  }

  const handleDeleteLeague = async (league: FootballLeague) => {
    if (!confirm(`لیگ «${league.name}» حذف شود؟ اگر مسابقه یا امتیازی داشته باشد، حذف انجام نمی‌شود.`)) return
    setError(null)
    try {
      await adminApi.deleteLeague(league.id)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف لیگ')
    }
  }

  const handleProcessResult = async () => {
    if (resultMatchId === null) return
    const homeScore = parseInt(resultHomeScore)
    const awayScore = parseInt(resultAwayScore)
    if (isNaN(homeScore) || isNaN(awayScore)) {
      setError('لطفاً نتیجه را به درستی وارد کنید')
      return
    }
    let raffleCount: number | undefined
    if (footballMode === 'big_match') {
      raffleCount = parseInt(resultRaffleCount, 10)
      if (isNaN(raffleCount) || raffleCount < 1) {
        setError('تعداد نفرات قرعه‌کشی باید عدد ۱ یا بیشتر باشد')
        return
      }
    }
    setError(null)
    try {
      // The league endpoint awards prediction points; for big match it also
      // draws the gold raffle among correct predictors using raffle_winner_count.
      await adminApi.processLeagueMatchResult(resultMatchId, homeScore, awayScore, raffleCount)
      setShowResultModal(false)
      setResultHomeTeam('')
      setResultAwayTeam('')
      setResultHomeScore('')
      setResultAwayScore('')
      setResultRaffleCount('5')
      setResultMatchId(null)
      if (activeTab === 'football') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ثبت نتیجه')
    }
  }

  const handleCreatePlayer = async () => {
    setError(null)
    try {
      if (editingPlayer) {
        await adminApi.updateFantasyPlayer(editingPlayer.id, newPlayer)
      } else {
        await adminApi.createFantasyPlayer(newPlayer)
      }
      setShowPlayerModal(false)
      setNewPlayer({ name: '', position: 'fwd', team: '', photo_url: '' })
      setPhotoPreview(null)
      setEditingPlayer(null)
      if (activeTab === 'fantasy') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره بازیکن')
    }
   }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    doUpload(file)
  }

  const doUpload = async (file: File) => {
    try {
      const res = await adminApi.uploadPlayerPhoto(file)
      setNewPlayer({ ...newPlayer, photo_url: res.url })
      setPhotoPreview(res.url)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در آپلود عکس')
    } finally {
      setPhotoUploading(false)
    }
  }

  const handleDeletePlayer = async (id: number) => {
    if (!window.confirm('آیا از حذف این بازیکن اطمینان دارید؟')) return
    setError(null)
    try {
      await adminApi.deleteFantasyPlayer(id)
      if (activeTab === 'fantasy') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف بازیکن')
    }
  }
  
  const handleCreateGroup = async () => {
    setError(null)
    try {
      if (editingGroup) {
        await adminApi.updatePlayerGroup(editingGroup.id, {
          title: newGroup.title,
          max_cards: parseInt(newGroup.max_cards) || 0,
          is_active: true,
        })
      } else {
        await adminApi.createPlayerGroup({
          title: newGroup.title,
          max_cards: parseInt(newGroup.max_cards) || 0,
        })
      }
      setShowGroupModal(false)
      setNewGroup({ title: '', max_cards: '' })
      setEditingGroup(null)
      if (activeTab === 'fantasy') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره گروه')
    }
  }

  const handleDeleteGroup = async (id: number) => {
    if (!window.confirm('آیا از حذف این گروه اطمینان دارید؟')) return
    setError(null)
    try {
      await adminApi.deletePlayerGroup(id)
      if (activeTab === 'fantasy') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف گروه')
    }
  }

  const handleOpenGroupPlayers = async (groupId: number) => {
    setSelectedGroupId(groupId)
    setError(null)
    try {
      const res = await adminApi.getGroupPlayers(groupId)
      setGroupPlayers(res.players)
      setShowGroupPlayersModal(true)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در بارگذاری بازیکنان گروه')
    }
  }

  const handleAddPlayerToGroup = async (playerId: number) => {
    if (selectedGroupId === null) return
    setError(null)
    try {
      await adminApi.addPlayerToGroup(selectedGroupId, playerId)
      handleOpenGroupPlayers(selectedGroupId)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در افزودن بازیکن')
    }
  }

  const handleRemovePlayerFromGroup = async (playerId: number) => {
    if (selectedGroupId === null) return
    setError(null)
    try {
      await adminApi.removePlayerFromGroup(selectedGroupId, playerId)
      handleOpenGroupPlayers(selectedGroupId)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف بازیکن')
    }
  }

  // A match usually kicks off later today, so the form opens on the next full
  // hour and the admin only has to move the date when it is another day.
  const localDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  const defaultKickoff = () => {
    const d = new Date()
    d.setHours(d.getHours() + 1, 0, 0, 0)
    return `${localDate(d)}T${String(d.getHours()).padStart(2, '0')}:00`
  }

  const kickoffDate = newMatch.start_time.slice(0, 10)
  const kickoffTime = newMatch.start_time.slice(11, 16)

  const setKickoff = (date: string, time: string) =>
    setNewMatch((prev) => ({ ...prev, start_time: `${date}T${time}` }))

  const handleOpenMatchGroups = async (matchId: number) => {
    setMatchGroupsMatchId(matchId)
    setMatchGroupsSelected([])
    setError(null)
    try {
      const res = await adminApi.getMatchGroups(matchId)
      setMatchGroupsSelected(res.groups.map((g) => g.id))
      setShowMatchGroupsModal(true)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در بارگذاری گروه‌های بازی')
    }
  }

  const handleToggleMatchGroup = (groupId: number) => {
    setMatchGroupsSelected((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    )
  }

  const handleSaveMatchGroups = async () => {
    if (matchGroupsMatchId === null) return
    setSavingMatchGroups(true)
    setError(null)
    try {
      await adminApi.setMatchGroups(matchGroupsMatchId, matchGroupsSelected)
      setShowMatchGroupsModal(false)
      setMatchGroupsMatchId(null)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره گروه‌های بازی')
    } finally {
      setSavingMatchGroups(false)
    }
  }

  const handleProcessTransfer = async (id: number, approve: boolean) => {
    const note = prompt(approve ? 'یادداشت تایید (اختیاری):' : 'دلیل رد (اختیاری):')
    if (note === null) return
    setError(null)
    try {
      await adminApi.processTransfer(id, approve, note)
      if (activeTab === 'transfers') fetchData()
      if (activeTab === 'dashboard') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در پردازش تراکنش')
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.user_id.trim() || !newMessage.text.trim()) return
    setError(null)
    try {
      await adminApi.sendMessage(parseInt(newMessage.user_id), newMessage.text)
      setShowMessageModal(false)
      setNewMessage({ user_id: '', text: '' })
      if (activeTab === 'messages') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ارسال پیام')
    }
  }

  const openUserDetail = async (userId: number) => {
    setError(null)
    setUserDetailLoading(true)
    setHistoryFilter('all')
    try {
      const detail = await adminApi.getUserDetail(userId)
      setSelectedUserDetail(detail)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دریافت جزئیات کاربر')
    } finally {
      setUserDetailLoading(false)
    }
  }

  const openTicketChat = async (id: number) => {
    setError(null)
    setTicketBusy(true)
    try {
      const res = await adminApi.getSupportTicket(id)
      setActiveTicket(res.ticket)
      setTicketReply('')
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در باز کردن تیکت')
    } finally {
      setTicketBusy(false)
    }
  }

  const handleTicketReply = async () => {
    if (!activeTicket || !ticketReply.trim()) return
    setTicketBusy(true)
    setError(null)
    try {
      await adminApi.replySupportTicket(activeTicket.id, ticketReply.trim())
      setTicketReply('')
      const res = await adminApi.getSupportTicket(activeTicket.id)
      setActiveTicket(res.ticket)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ارسال پاسخ')
    } finally {
      setTicketBusy(false)
    }
  }


  const handleGenerateGuessNumber = async () => {
    setError(null)
    setGeneratingGuessNumber(true)
    try {
      await adminApi.generateGuessNumber()
      if (activeTab === 'guess-number') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در تولید عدد')
    } finally {
      setGeneratingGuessNumber(false)
    }
  }

  const handleRevealGuessNumber = async () => {
    setError(null)
    try {
      await adminApi.revealGuessNumber()
      if (activeTab === 'guess-number') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در افشای عدد')
    }
  }

  const handleSetMorseWords = async () => {
    const words = morseWordsInput.map((w) => w.trim().toUpperCase())
    if (words.some((w) => !w) || words.length !== 5) {
      setError('۵ کلمه انگلیسی لازم است')
      return
    }
    setError(null)
    try {
      await adminApi.setMorseWords(words)
      if (activeTab === 'morse') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ثبت کلمات مورس')
    }
  }

  const renderDashboard = () => {
    if (!stats) return <div className="text-center py-8 text-gray-400">⏳ در حال بارگذاری...</div>
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <StatCard icon="👥" label="کل کاربران" value={formatNumber(stats.total_users)} color="blue" />
          <StatCard icon="🆕" label="کاربران امروز" value={formatNumber(stats.new_users_today)} color="green" />
          <StatCard icon="📅" label="کاربران این هفته" value={formatNumber(stats.new_users_week)} color="purple" />
          <StatCard icon="📆" label="کاربران این ماه" value={formatNumber(stats.new_users_month)} color="orange" />
        </div>

        <Card>
          <h3 className="font-bold mb-3">💰 آمار کوین‌ها</h3>
          <div className="grid grid-cols-2 gap-3">
            <StatCard icon="🥉" label="برنز" value={formatNumber(stats.total_coins.bronze)} color="amber" />
            <StatCard icon="🥈" label="نقره" value={formatNumber(stats.total_coins.silver)} color="gray" />
            <StatCard icon="🥇" label="طلا" value={formatNumber(stats.total_coins.gold)} color="yellow" />
            <StatCard icon="💎" label="الماس" value={formatNumber(stats.total_coins.diamond)} color="cyan" />
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Card>
            <h3 className="font-bold mb-3">🎯 حدس عدد</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>امروز</span><span className="font-bold">{stats.guess_number_stats.today_guesses}</span></div>
              <div className="flex justify-between"><span>برنده‌های امروز</span><span className="font-bold">{stats.guess_number_stats.today_winners}</span></div>
              <div className="flex justify-between"><span>کل برنده‌ها</span><span className="font-bold">{stats.guess_number_stats.total_winners}</span></div>
              <div className="flex justify-between"><span>کل بازی‌ها</span><span className="font-bold">{stats.guess_number_stats.total_games_played}</span></div>
            </div>
          </Card>

          <Card>
            <h3 className="font-bold mb-3">🪂 آردراپ</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>کلیک‌های امروز</span><span className="font-bold">{formatNumber(stats.airdrop_stats.today_total_clicks)}</span></div>
              <div className="flex justify-between"><span>مشارکت‌کنندگان امروز</span><span className="font-bold">{stats.airdrop_stats.today_participants}</span></div>
              <div className="flex justify-between"><span>کلیک‌های ماه</span><span className="font-bold">{formatNumber(stats.airdrop_stats.month_total_clicks)}</span></div>
              <div className="flex justify-between"><span>مشارکت‌کنندگان ماه</span><span className="font-bold">{stats.airdrop_stats.month_participants}</span></div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Card>
            <h3 className="font-bold mb-3">⚽ فوتبال</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>کل مسابقات</span><span className="font-bold">{stats.football_stats.total_matches}</span></div>
              <div className="flex justify-between text-green-400"><span>در انتظار</span><span className="font-bold">{stats.football_stats.upcoming_matches}</span></div>
              <div className="flex justify-between text-yellow-400"><span>زنده</span><span className="font-bold">{stats.football_stats.live_matches}</span></div>
              <div className="flex justify-between text-gray-400"><span>تمام‌شده</span><span className="font-bold">{stats.football_stats.finished_matches}</span></div>
              <div className="flex justify-between"><span>کل پیش‌بینی‌ها</span><span className="font-bold">{stats.football_stats.total_predictions}</span></div>
            </div>
          </Card>

          <Card>
            <h3 className="font-bold mb-3">🏆 فانتزی</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>بازیکنان</span><span className="font-bold">{stats.fantasy_stats.total_players}</span></div>
              <div className="flex justify-between"><span>تیم‌ها</span><span className="font-bold">{stats.fantasy_stats.total_teams}</span></div>
              <div className="flex justify-between"><span>رأی‌ها</span><span className="font-bold">{stats.fantasy_stats.total_votes}</span></div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Card>
            <h3 className="font-bold mb-3">💸 تراکنش‌ها</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-yellow-400"><span>در انتظار</span><span className="font-bold">{stats.transfer_stats.pending_transfers}</span></div>
              <div className="flex justify-between text-green-400"><span>تایید شده</span><span className="font-bold">{stats.transfer_stats.approved_transfers}</span></div>
              <div className="flex justify-between text-red-400"><span>رد شده</span><span className="font-bold">{stats.transfer_stats.rejected_transfers}</span></div>
              <div className="flex justify-between text-blue-400"><span>تکمیل شده</span><span className="font-bold">{stats.transfer_stats.completed_transfers}</span></div>
            </div>
          </Card>

          <Card>
            <h3 className="font-bold mb-3">🎫 کدهای پاداش</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>کل کدها</span><span className="font-bold">{stats.reward_code_stats.total_codes}</span></div>
              <div className="flex justify-between text-green-400"><span>فعال</span><span className="font-bold">{stats.reward_code_stats.active_codes}</span></div>
              <div className="flex justify-between text-gray-400"><span>استفاده شده</span><span className="font-bold">{stats.reward_code_stats.used_codes}</span></div>
              <div className="flex justify-between"><span>روزانه</span><span className="font-bold">{stats.reward_code_stats.daily_codes}</span></div>
              <div className="flex justify-between"><span>ماهانه</span><span className="font-bold">{stats.reward_code_stats.monthly_codes}</span></div>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  const renderCodes = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold">🎫 کدهای پاداش ({codes.length})</h3>
        <Button variant="primary" onClick={() => setShowCodeModal(true)}>➕ کد جدید</Button>
      </div>
      {codes.length === 0 ? (
        <Card><div className="text-center py-6 text-gray-400">هیچ کدی یافت نشد</div></Card>
      ) : (
        <div className="space-y-3">
          {codes.map((code) => (
            <Card key={code.id} className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  code.award_type === 'daily' ? 'bg-blue-900/30 text-blue-300' : 'bg-purple-900/30 text-purple-300'
                }`}>
                  {code.award_type === 'daily' ? 'روزانه' : 'ماهانه'}
                </span>
                {code.is_used ? (
                  <span className="text-xs text-gray-500">✅ استفاده شده</span>
                ) : (
                  <span className="text-xs text-green-400">✅ فعال</span>
                )}
              </div>
              <div className="font-mono text-center text-sm bg-gray-900/50 px-3 py-2 rounded-lg mb-2">{code.code}</div>
              <div className="text-xs text-gray-500">
                کاربر: #{code.user_id} | صادره: {formatDate(code.created_at)} |
                {(!code.valid_to || new Date(code.valid_to).getTime() < Date.UTC(1970, 0, 2))
                  ? 'بدون انقضا (تا زمان استفاده)'
                  : `معتبر: ${formatDate(code.valid_from)} تا ${formatDate(code.valid_to)}`}
              </div>
              {code.selected_prize && (
                <div className="mt-2 text-xs text-yellow-300">جایزه انتخابی: {code.selected_prize}</div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )

  const handleSaveGiftPrize = async () => {
    setError(null)
    if (!giftForm.title.trim()) {
      setError('عنوان جایزه لازم است')
      return
    }
    try {
      if (editingGift) {
        await adminApi.updateGiftPrize(editingGift.id, {
          title: giftForm.title,
          description: giftForm.description,
          image_url: giftForm.image_url,
          is_active: editingGift.is_active,
          sort_order: giftForm.sort_order,
        })
      } else {
        await adminApi.createGiftPrize({
          title: giftForm.title,
          description: giftForm.description,
          image_url: giftForm.image_url,
          sort_order: giftForm.sort_order,
        })
      }
      setGiftForm({ title: '', description: '', image_url: '', sort_order: 0 })
      setEditingGift(null)
      if (activeTab === 'gifts') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره جایزه')
    }
  }

  const handleToggleGiftPrize = async (prize: GiftPrize) => {
    try {
      await adminApi.updateGiftPrize(prize.id, {
        title: prize.title,
        description: prize.description,
        image_url: prize.image_url,
        is_active: !prize.is_active,
        sort_order: prize.sort_order,
      })
      if (activeTab === 'gifts') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در تغییر وضعیت جایزه')
    }
  }

  const handleDeleteGiftPrize = async (id: number) => {
    if (!confirm('این جایزه حذف شود؟')) return
    try {
      await adminApi.deleteGiftPrize(id)
      if (activeTab === 'gifts') fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف جایزه')
    }
  }

  const handleGiftPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setGiftPhotoUploading(true)
    setError(null)
    try {
      const res = await adminApi.uploadPlayerPhoto(file)
      setGiftForm((prev) => ({ ...prev, image_url: res.url }))
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در آپلود عکس جایزه')
    } finally {
      setGiftPhotoUploading(false)
    }
  }

  const renderGifts = () => (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="font-bold mb-3">{editingGift ? 'ویرایش جایزه' : 'جایزه جدید'}</h3>
        <div className="space-y-2">
          <Input placeholder="عنوان" value={giftForm.title} onChange={(e) => setGiftForm({ ...giftForm, title: e.target.value })} />
          <Input placeholder="توضیح" value={giftForm.description} onChange={(e) => setGiftForm({ ...giftForm, description: e.target.value })} />
          <div className="p-3 bg-gray-900/40 rounded-xl border border-gray-700">
            <p className="text-sm text-gray-400 mb-2">عکس جایزه</p>
            {giftForm.image_url ? (
              <div className="flex items-center gap-3 mb-3">
                <img src={giftForm.image_url} alt="" className="w-20 h-20 rounded-xl object-cover border border-gray-600" />
                <button
                  type="button"
                  className="text-xs px-2 py-1 bg-gray-700 rounded"
                  onClick={() => setGiftForm({ ...giftForm, image_url: '' })}
                >
                  حذف عکس
                </button>
              </div>
            ) : (
              <div className="w-20 h-20 mb-3 rounded-xl bg-gray-800 flex items-center justify-center text-2xl">🎁</div>
            )}
            <label className="inline-flex items-center gap-2 text-sm px-3 py-2 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={handleGiftPhotoUpload}
                disabled={giftPhotoUploading}
              />
              {giftPhotoUploading ? 'در حال آپلود...' : 'بارگذاری عکس'}
            </label>
            <p className="text-xs text-gray-500 mt-2">jpg / png / webp · حداکثر ۵ مگابایت</p>
          </div>
          <Input
            type="number"
            placeholder="ترتیب"
            value={String(giftForm.sort_order)}
            onChange={(e) => setGiftForm({ ...giftForm, sort_order: parseInt(e.target.value || '0', 10) })}
          />
          <div className="flex gap-2">
            <Button variant="primary" onClick={handleSaveGiftPrize}>{editingGift ? 'ذخیره' : 'ثبت جایزه'}</Button>
            {editingGift && (
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingGift(null)
                  setGiftForm({ title: '', description: '', image_url: '', sort_order: 0 })
                }}
              >
                لغو
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="font-bold mb-3">جوایز تعریف‌شده ({giftPrizes.length})</h3>
        {giftPrizes.length === 0 ? (
          <div className="text-center py-6 text-gray-400">هنوز جایزه‌ای تعریف نشده</div>
        ) : (
          <div className="space-y-2">
            {giftPrizes.map((prize) => (
              <div key={prize.id} className="p-3 bg-gray-800/40 rounded-xl">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {prize.image_url ? (
                      <img src={prize.image_url} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gray-700 flex items-center justify-center shrink-0">🎁</div>
                    )}
                    <div className="min-w-0">
                      <div className="font-bold">{prize.title}</div>
                      {prize.description && <div className="text-sm text-gray-400 mt-1">{prize.description}</div>}
                      <div className="text-xs text-gray-500 mt-1">
                        {prize.is_active ? 'فعال' : 'غیرفعال'} · ترتیب {prize.sort_order}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      className="text-xs px-2 py-1 bg-gray-700 rounded"
                      onClick={() => {
                        setEditingGift(prize)
                        setGiftForm({
                          title: prize.title,
                          description: prize.description || '',
                          image_url: prize.image_url || '',
                          sort_order: prize.sort_order,
                        })
                      }}
                    >
                      ویرایش
                    </button>
                    <button type="button" className="text-xs px-2 py-1 bg-gray-700 rounded" onClick={() => handleToggleGiftPrize(prize)}>
                      {prize.is_active ? 'غیرفعال' : 'فعال'}
                    </button>
                    <button type="button" className="text-xs px-2 py-1 bg-red-900/40 text-red-300 rounded" onClick={() => handleDeleteGiftPrize(prize.id)}>
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-bold mb-3">برندگان و جایزه انتخابی ({giftWinners.length})</h3>
        {giftWinners.length === 0 ? (
          <div className="text-center py-6 text-gray-400">هنوز کسی جایزه‌ای انتخاب نکرده</div>
        ) : (
          <div className="space-y-2">
            {giftWinners.map((winner) => (
              <div key={winner.id} className="p-3 bg-gray-800/40 rounded-xl">
                <div className="font-mono text-sm" dir="ltr">{winner.code}</div>
                <div className="text-sm mt-1">
                  کاربر #{winner.used_by || winner.user_id}
                  {winner.selected_prize ? ` · جایزه: ${winner.selected_prize}` : ''}
                </div>
                <div className="text-xs text-gray-500 mt-1">{winner.used_at || formatDate(winner.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )

  const renderScores = () => (
    <Card>
      <h3 className="font-bold mb-3">📈 امتیازهای امروز ({today})</h3>
      {scores.length === 0 ? (
        <div className="text-center py-6 text-gray-400">هیچ امتیازی یافت نشد</div>
      ) : (
        <div className="space-y-2">
          {scores.map((entry) => (
            <div key={entry.user_id} className="flex items-center justify-between p-2 bg-gray-800/30 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="w-8 text-center font-bold text-yellow-400">
                  {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                </span>
                <span className="font-medium">{entry.first_name || entry.username || `#${entry.user_id}`}</span>
              </div>
              <span className="font-bold">{formatNumber(entry.score)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )

  const renderMonthly = () => (
    <Card>
      <h3 className="font-bold mb-3">📊 امتیازهای ماهانه</h3>
      {monthlyScores.length === 0 ? (
        <div className="text-center py-6 text-gray-400">هیچ امتیازی یافت نشد</div>
      ) : (
        <div className="space-y-2">
          {monthlyScores.map((entry) => (
            <div key={entry.user_id} className="flex items-center justify-between p-2 bg-gray-800/30 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="w-8 text-center font-bold text-yellow-400">
                  {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                </span>
                <span className="font-medium">{entry.first_name || entry.username || `#${entry.user_id}`}</span>
              </div>
              <span className="font-bold">{formatNumber(entry.score)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )

  const renderUsers = () => {
    const coins: CoinMap = selectedUserDetail?.wallet?.coins || { bronze: 0, silver: 0, gold: 0, diamond: 0 }
    const filteredHistory = (selectedUserDetail?.history || []).filter((item) =>
      historyFilter === 'all' ? true : item.kind === historyFilter
    )

    return (
      <div>
        <div className="flex gap-2 mb-4">
          <Input
            placeholder="جستجوی کاربران..."
            value={usersSearch}
            onChange={(e) => setUsersSearch(e.target.value)}
            className="flex-1"
          />
        </div>
        <Card>
          {users.length === 0 ? (
            <div className="text-center py-6 text-gray-400">هیچ کاربری یافت نشد</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="pb-2">ID</th>
                      <th className="pb-2">نام</th>
                      <th className="pb-2">یوزرنیم</th>
                      <th className="pb-2">لول</th>
                      <th className="pb-2">دعوت‌ها</th>
                      <th className="pb-2">ادمین</th>
                      <th className="pb-2">آخرین ورود</th>
                      <th className="pb-2">جزئیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-gray-800 hover:bg-gray-800/40 cursor-pointer"
                        onClick={() => openUserDetail(user.id)}
                      >
                        <td className="py-2 text-gray-400" dir="ltr">{user.public_id || `#${user.id}`}</td>
                        <td className="py-2">{user.first_name} {user.last_name}</td>
                        <td className="py-2 text-gray-400">@{user.username}</td>
                        <td className="py-2"><span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs">{user.level}</span></td>
                        <td className="py-2 text-yellow-400">{user.referral_count}</td>
                        <td className="py-2">{user.is_admin ? '✅' : '❌'}</td>
                        <td className="py-2 text-gray-400">{formatDate(user.last_login)}</td>
                        <td className="py-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="!w-auto"
                            onClick={(e) => {
                              e.stopPropagation()
                              openUserDetail(user.id)
                            }}
                          >
                            مشاهده
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                <span className="text-sm text-gray-400">صفحه {usersPage + 1} از {Math.ceil(usersTotal / 20)}</span>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setUsersPage(p => Math.max(0, p - 1))} disabled={usersPage === 0}>قبلی</Button>
                  <Button variant="secondary" size="sm" onClick={() => setUsersPage(p => Math.min(Math.ceil(usersTotal / 20) - 1, p + 1))} disabled={usersPage >= Math.ceil(usersTotal / 20) - 1}>بعدی</Button>
                </div>
              </div>
            </>
          )}
        </Card>

        <Modal
          open={!!selectedUserDetail || userDetailLoading}
          onClose={() => {
            if (!userDetailLoading) setSelectedUserDetail(null)
          }}
          title={
            selectedUserDetail
              ? `جزئیات ${selectedUserDetail.user.first_name || selectedUserDetail.user.username || `#${selectedUserDetail.user.id}`}`
              : 'جزئیات کاربر'
          }
        >
          {userDetailLoading || !selectedUserDetail ? (
            <div className="text-center py-8 text-gray-400">در حال بارگذاری...</div>
          ) : (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-800/40 p-3 rounded-lg">
                  <div className="text-xs text-gray-400">شناسه داخلی</div>
                  <div className="font-bold" dir="ltr">#{selectedUserDetail.user.id}</div>
                </div>
                <div className="bg-gray-800/40 p-3 rounded-lg">
                  <div className="text-xs text-gray-400">شناسه عمومی</div>
                  <div className="font-bold" dir="ltr">{selectedUserDetail.user.public_id || '—'}</div>
                </div>
                <div className="bg-gray-800/40 p-3 rounded-lg">
                  <div className="text-xs text-gray-400">یوزرنیم</div>
                  <div className="font-bold">@{selectedUserDetail.user.username || '—'}</div>
                </div>
                <div className="bg-gray-800/40 p-3 rounded-lg">
                  <div className="text-xs text-gray-400">دعوت‌ها</div>
                  <div className="font-bold text-yellow-400">{formatNumber(selectedUserDetail.scores.referral_count)}</div>
                </div>
                <div className="bg-gray-800/40 p-3 rounded-lg col-span-2">
                  <div className="text-xs text-gray-400">رمز کیف پول (PIN)</div>
                  <div className="font-bold font-mono tracking-widest" dir="ltr">
                    {selectedUserDetail.wallet_pin || 'تنظیم نشده'}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-2">موجودی سکه‌ها</h4>
                <div className="grid grid-cols-2 gap-2">
                  {(['gold', 'diamond', 'silver', 'bronze'] as const).map((coinType) => (
                    <div key={coinType} className="bg-gray-800/40 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                        <span>{getCoinIcon(coinType)}</span>
                        <span>{getCoinName(coinType)}</span>
                      </div>
                      <div className="text-xl font-bold">{formatNumber(coins[coinType] || 0)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-sm text-gray-400">
                  دلار کیف پول: <span className="text-green-400 font-bold">${(selectedUserDetail.wallet?.dollar || 0).toFixed(2)}</span>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-2">امتیازها</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-gray-800/40 p-3 rounded-lg">
                    <div className="text-xs text-gray-400">ایردراپ امروز</div>
                    <div className="font-bold">{formatNumber(selectedUserDetail.scores.airdrop_today)}</div>
                  </div>
                  <div className="bg-gray-800/40 p-3 rounded-lg">
                    <div className="text-xs text-gray-400">جمع ایردراپ</div>
                    <div className="font-bold">{formatNumber(selectedUserDetail.scores.airdrop_total)}</div>
                  </div>
                </div>
                {selectedUserDetail.scores.football.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {selectedUserDetail.scores.football.map((fs) => (
                      <div key={fs.league_id} className="bg-gray-800/30 p-2 rounded-lg text-sm flex justify-between gap-2">
                        <span className="text-gray-300">{fs.league_name}</span>
                        <span className="font-bold text-primary">{formatNumber(fs.total_score)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {selectedUserDetail.scores.horse_league.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {selectedUserDetail.scores.horse_league.map((hs) => (
                      <div key={`${hs.user_id}-${hs.month_key}`} className="bg-gray-800/30 p-2 rounded-lg text-sm flex justify-between gap-2">
                        <span className="text-gray-300">اسب · {hs.month_key}</span>
                        <span className="font-bold text-yellow-400">{formatNumber(hs.total_score)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <h4 className="font-bold">تاریخچه</h4>
                  <div className="flex gap-1 flex-wrap">
                    {([
                      ['all', 'همه'],
                      ['activity', 'فعالیت'],
                      ['coin', 'سکه'],
                      ['score', 'امتیاز'],
                      ['gift', 'جایزه'],
                      ['transfer', 'انتقال'],
                    ] as const).map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setHistoryFilter(id)}
                        className={`px-2 py-1 rounded text-xs ${
                          historyFilter === id ? 'bg-primary text-white' : 'bg-gray-800 text-gray-300'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {filteredHistory.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 text-sm">موردی ثبت نشده</div>
                ) : (
                  <div className="space-y-2">
                    {filteredHistory.map((item, idx) => (
                      <div key={`${item.source}-${item.at}-${idx}`} className="bg-gray-800/40 p-3 rounded-lg text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-medium">{item.title}</div>
                            {item.detail && <div className="text-xs text-gray-400 mt-0.5">{item.detail}</div>}
                            <div className="text-xs text-gray-500 mt-1" dir="ltr">{formatDate(item.at)}</div>
                          </div>
                          <div className="text-left shrink-0">
                            {item.kind === 'activity' ? (
                              <span className="text-cyan-400 font-bold text-xs">فعالیت</span>
                            ) : item.kind === 'score' ? (
                              <span className="text-primary font-bold">+{formatNumber(item.score || 0)} امتیاز</span>
                            ) : item.coin_type ? (
                              <span className={`font-bold ${item.direction === 'out' ? 'text-red-400' : 'text-green-400'}`}>
                                {item.direction === 'out' ? '−' : '+'}
                                {formatNumber(item.amount || 0)} {getCoinIcon(item.coin_type)} {getCoinName(item.coin_type)}
                              </span>
                            ) : item.kind === 'gift' ? (
                              <span className="text-yellow-400 font-bold">🎁 جایزه</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
      </div>
    )
  }

  const renderGuessNumber = () => (
    <div className="space-y-4">
      <Card>
        <h3 className="font-bold mb-3">🎯 مدیریت عدد روزانه</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-800/30 p-3 rounded-lg">
              <div className="text-xs text-gray-400">وضعیت</div>
              <div className="font-bold">{guessStatus?.has_active_number ? '✅ فعال' : '❌ غیرفعال'}</div>
            </div>
            <div className="bg-gray-800/30 p-3 rounded-lg">
              <div className="text-xs text-gray-400">افشا شده</div>
              <div className="font-bold">{guessStatus?.is_revealed ? '✅ بله' : '❌ خیر'}</div>
            </div>
            <div className="bg-gray-800/30 p-3 rounded-lg">
              <div className="text-xs text-gray-400">پایان روز</div>
              <div className="font-bold">{guessStatus?.end_of_day ? '✅ بله' : '❌ خیر'}</div>
            </div>
            <div className="bg-gray-800/30 p-3 rounded-lg">
              <div className="text-xs text-gray-400">برنده‌ها</div>
              <div className="font-bold">{guessStatus?.winners_count || 0}</div>
            </div>
          </div>

          <div className="bg-gray-800/40 p-4 rounded-lg text-center">
            <div className="text-xs text-gray-400 mb-1">عدد امروز (ساخته‌شده توسط سرور · بدون رقم ۴)</div>
            <div className="font-mono text-2xl font-bold tracking-widest" dir="ltr">
              {guessStatus?.current_number || '— — — — — — —'}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              عدد فقط توسط سرور تولید می‌شود؛ تنظیم دستی و رقم ۴ وجود ندارد.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={handleGenerateGuessNumber}
              disabled={generatingGuessNumber || !!guessStatus?.has_active_number}
              className="!w-auto"
            >
              {generatingGuessNumber ? 'در حال تولید...' : '🎲 تولید عدد توسط سرور'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleRevealGuessNumber}
              disabled={!guessStatus?.has_active_number || guessStatus?.is_revealed}
              className="!w-auto"
            >
              🔓 افشا کردن
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="font-bold mb-3">🏆 برندگان (کل زمان)</h3>
        {guessWinners.length === 0 ? (
          <div className="text-center py-6 text-gray-400">هیچ برنده‌ای یافت نشد</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="pb-2">رتبه</th>
                  <th className="pb-2">کاربر</th>
                  <th className="pb-2">عدد حدس زده</th>
                  <th className="pb-2">امتیاز</th>
                  <th className="pb-2">تاریخ</th>
                </tr>
              </thead>
              <tbody>
                {guessWinners.map((w, i) => (
                  <tr key={w.id} className="border-b border-gray-800">
                    <td className="py-2 text-yellow-400 font-bold">{i + 1}</td>
                    <td className="py-2">#{w.user_id}</td>
                    <td className="py-2 font-mono">{w.guess_number}</td>
                    <td className="py-2">{formatNumber(w.score)}</td>
                    <td className="py-2 text-gray-400">{formatDate(w.played_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )

  const renderMorse = () => (
    <div className="space-y-4">
      <Card>
        <h3 className="font-bold mb-3">📡 کلمات مورس امروز</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-gray-800/30 p-3 rounded-lg">
            <div className="text-xs text-gray-400">تاریخ</div>
            <div className="font-bold">{morseStatus?.date || '—'}</div>
          </div>
          <div className="bg-gray-800/30 p-3 rounded-lg">
            <div className="text-xs text-gray-400">پنجره بازی</div>
            <div className="font-bold">{morseStatus?.window_open ? '✅ باز' : '❌ بسته'}</div>
          </div>
          <div className="bg-gray-800/30 p-3 rounded-lg">
            <div className="text-xs text-gray-400">حدس‌های درست امروز</div>
            <div className="font-bold">{morseStatus?.solves_today ?? 0}</div>
          </div>
          <div className="bg-gray-800/30 p-3 rounded-lg">
            <div className="text-xs text-gray-400">وضعیت</div>
            <div className="font-bold">{morseStatus?.status || '—'}</div>
          </div>
        </div>
        <div className="space-y-2 mb-3">
          {morseWordsInput.map((w, i) => (
            <Input
              key={i}
              placeholder={`کلمه ${i + 1} (انگلیسی)`}
              value={w}
              onChange={(e) => {
                const next = [...morseWordsInput]
                next[i] = e.target.value.toUpperCase()
                setMorseWordsInput(next)
              }}
              className="font-mono"
            />
          ))}
        </div>
        <Button variant="primary" onClick={handleSetMorseWords}>💾 ثبت ۵ کلمه امروز</Button>
        <p className="text-xs text-gray-500 mt-2">پیش‌فرض: HELLO WORLD MORSE CODE GAME</p>
      </Card>
    </div>
  )

  const kickoffPassed = (match: FootballMatch) =>
    match.status === 'upcoming' && new Date(match.start_time).getTime() <= Date.now()

  const modeLeagues = leagues.filter((l) => l.mode === footballMode)
  const activeLeague = modeLeagues.find((l) => l.is_active) || null
  const inactiveLeagues = modeLeagues.filter((l) => !l.is_active)

  const modeMatches = matches.filter((m) => m.mode === footballMode)
  const visibleMatches = modeMatches.filter((m) => {
    if (matchStatusFilter === 'all') return true
    if (matchStatusFilter === 'cancelled') return m.status === 'cancelled'
    if (matchStatusFilter === 'finished') return m.status === 'finished'
    if (matchStatusFilter === 'started') return m.status === 'upcoming' && kickoffPassed(m)
    return m.status === 'upcoming' && !kickoffPassed(m)
  })

  const matchStatusCounts = {
    all: modeMatches.length,
    pending: modeMatches.filter((m) => m.status === 'upcoming' && !kickoffPassed(m)).length,
    started: modeMatches.filter((m) => m.status === 'upcoming' && kickoffPassed(m)).length,
    finished: modeMatches.filter((m) => m.status === 'finished').length,
    cancelled: modeMatches.filter((m) => m.status === 'cancelled').length,
  }

  const modeLabel = footballMode === 'big_match' ? 'بازی بزرگ' : 'لیگ روزانه'
  const modeHint =
    footballMode === 'big_match'
      ? 'مسابقه‌های هفتگی + تیم رویایی. اول لیگ این حالت را فعال کن، بعد مسابقه بساز و گروه کارت‌ها را وصل کن.'
      : 'مسابقه‌های روزانه + نظرسنجی و امتیاز تجمعی. اول لیگ این حالت را فعال کن، بعد مسابقه بساز.'

  const renderFootball = () => (
    <div className="space-y-4">
      <div className="flex gap-2">
        {([
          ['league', '🏆 لیگ روزانه'],
          ['big_match', '⭐ بازی بزرگ'],
        ] as const).map(([mode, label]) => (
          <Button
            key={mode}
            variant={footballMode === mode ? 'primary' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => {
              setFootballMode(mode)
              setNewMatch((prev) => ({ ...prev, mode }))
              setMatchesPage(0)
              setMatchStatusFilter('pending')
            }}
          >
            {label}
          </Button>
        ))}
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">{modeLabel}</h3>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">{modeHint}</p>
          </div>
          <span
            className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${
              activeLeague
                ? 'bg-emerald-900/40 text-emerald-300'
                : 'bg-amber-900/40 text-amber-200'
            }`}
          >
            {activeLeague ? 'لیگ فعال ✓' : 'لیگ فعال نیست'}
          </span>
        </div>

        {activeLeague ? (
          <div className="rounded-xl bg-gray-800/50 p-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm">
              <div className="font-medium">{activeLeague.name}</div>
              <div className="text-xs text-gray-400 mt-0.5">
                {formatDate(activeLeague.start_date)} — {formatDate(activeLeague.end_date)}
              </div>
            </div>
            <div className="flex gap-1 flex-wrap">
              <Button variant="secondary" size="sm" className="!w-auto" onClick={() => handleOpenLeague(activeLeague)}>
                ویرایش
              </Button>
              <Button variant="secondary" size="sm" className="!w-auto" onClick={() => handleToggleLeagueActive(activeLeague)}>
                غیرفعال کردن
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 space-y-2">
            <p className="text-sm text-amber-100">
              برای ساخت مسابقه در «{modeLabel}» باید یک لیگ فعال داشته باشی.
            </p>
            <Button
              variant="primary"
              size="sm"
              className="!w-auto"
              disabled={creatingLeague}
              onClick={handleEnsureActiveLeague}
            >
              {creatingLeague
                ? 'در حال آماده‌سازی…'
                : inactiveLeagues.length > 0
                  ? 'فعال‌سازی لیگ موجود'
                  : 'ساخت و فعال‌سازی لیگ'}
            </Button>
            {inactiveLeagues.length > 0 && (
              <div className="space-y-1 pt-1">
                {inactiveLeagues.slice(0, 3).map((league) => (
                  <div key={league.id} className="flex items-center justify-between gap-2 text-xs text-gray-300">
                    <span>{league.name} (غیرفعال)</span>
                    <div className="flex gap-1">
                      <Button variant="secondary" size="sm" className="!w-auto" onClick={() => handleToggleLeagueActive(league)}>
                        فعال
                      </Button>
                      <Button variant="secondary" size="sm" className="!w-auto" onClick={() => handleOpenLeague(league)}>
                        ویرایش
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h4 className="font-bold">باشگاه‌ها و پرچم‌ها · {clubs.length}</h4>
            <p className="text-xs text-gray-400 mt-1">کاتالوگ تیم‌ها برای ساخت مسابقه؛ پرچم در اپ کاربر نمایش داده می‌شود.</p>
          </div>
          <Button variant="secondary" size="sm" className="!w-auto" onClick={() => { setShowInlineClub(null); openClubModal() }}>
            ➕ باشگاه جدید
          </Button>
        </div>
        {clubs.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-3">هنوز باشگاهی ثبت نشده</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
            {clubs.map((club, index) => (
              <div key={club.id} className="flex items-center gap-2 rounded-lg bg-gray-800/50 px-2.5 py-2">
                <span className="w-7 shrink-0 text-center text-xs tabular-nums text-gray-400">{index + 1}</span>
                {club.flag_url ? (
                  <img src={club.flag_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded bg-gray-700 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{club.name}</div>
                  {!club.is_active && <div className="text-[10px] text-amber-300">غیرفعال</div>}
                </div>
                <Button variant="secondary" size="sm" className="!w-auto !px-2" onClick={() => openClubModal(club)}>✏️</Button>
                <Button variant="secondary" size="sm" className="!w-auto !px-2" onClick={() => handleDeleteClub(club)}>🗑</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="font-bold">مسابقه‌ها · {modeLabel}</h4>
        <Button
          variant="primary"
          size="sm"
          className="!w-auto"
          disabled={!activeLeague}
          onClick={() => {
            if (!activeLeague) {
              setError(`اول لیگ «${modeLabel}» را فعال یا بساز`)
              return
            }
            setNewMatch(emptyMatchForm(footballMode))
            setShowInlineClub(null)
            setShowMatchModal(true)
          }}
        >
          ➕ مسابقه جدید
        </Button>
      </div>

      {!activeLeague && (
        <p className="text-xs text-amber-300/90 -mt-2">
          تا وقتی لیگ فعال نباشد، ساخت مسابقه برای این حالت ممکن نیست.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {([
          ['pending', 'پیش‌رو'],
          ['started', 'شروع‌شده'],
          ['finished', 'تمام‌شده'],
          ['all', 'همه'],
          ['cancelled', 'لغو'],
        ] as const).map(([key, label]) => (
          <Button
            key={key}
            variant={matchStatusFilter === key ? 'primary' : 'outline'}
            size="sm"
            className="!w-auto"
            onClick={() => setMatchStatusFilter(key)}
          >
            {label} ({matchStatusCounts[key]})
          </Button>
        ))}
      </div>

      <Card>
        {visibleMatches.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm space-y-2">
            <p>
              {modeMatches.length === 0
                ? `هنوز مسابقه‌ای برای ${modeLabel} نیست`
                : 'در این فیلتر مسابقه‌ای نیست'}
            </p>
            {activeLeague && modeMatches.length === 0 && (
              <Button
                variant="secondary"
                size="sm"
                className="!w-auto mx-auto"
                onClick={() => {
                  setNewMatch(emptyMatchForm(footballMode))
                  setShowInlineClub(null)
                  setShowMatchModal(true)
                }}
              >
                ساخت اولین مسابقه
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {visibleMatches.map((match) => (
                <div key={match.id} className="rounded-xl border border-gray-700/80 bg-gray-900/40 p-3 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {match.home_club?.flag_url && (
                          <img src={match.home_club.flag_url} alt="" className="w-5 h-5 rounded object-cover" />
                        )}
                        <span>{match.home_team}</span>
                        <span className="text-gray-500">vs</span>
                        {match.away_club?.flag_url && (
                          <img src={match.away_club.flag_url} alt="" className="w-5 h-5 rounded object-cover" />
                        )}
                        <span>{match.away_team}</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <span>#{match.id}</span>
                        <span>{formatDate(match.start_time)}</span>
                        <span>
                          {match.status === 'finished'
                            ? `نتیجه ${match.home_score} - ${match.away_score}`
                            : match.status === 'upcoming'
                              ? kickoffPassed(match)
                                ? 'شروع شده — منتظر ثبت نتیجه'
                                : 'در انتظار شروع'
                              : match.status === 'cancelled'
                                ? 'لغو شده'
                                : match.status}
                        </span>
                        {match.raffle_drawn ? <span className="text-emerald-400">قرعه‌کشی انجام شده</span> : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {footballMode === 'big_match' && (
                      <Button variant="secondary" size="sm" className="!w-auto" onClick={() => handleOpenMatchGroups(match.id)}>
                        ۱. گروه کارت‌ها
                      </Button>
                    )}
                    {match.status !== 'finished' && match.status !== 'cancelled' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="!w-auto"
                        onClick={() => {
                          setResultMatchId(match.id)
                          setResultHomeTeam(match.home_team)
                          setResultAwayTeam(match.away_team)
                          setShowResultModal(true)
                          setResultHomeScore('')
                          setResultAwayScore('')
                          setResultRaffleCount('5')
                        }}
                      >
                        {footballMode === 'big_match' ? '۲. ثبت نتیجه' : 'ثبت نتیجه'}
                      </Button>
                    )}
                    {footballMode === 'big_match' && (
                      <Button variant="secondary" size="sm" className="!w-auto" onClick={() => handleOpenPlayerStats(match.id)}>
                        ۳. آمار بازیکنان
                      </Button>
                    )}
                    {footballMode === 'big_match' && match.status === 'finished' && (
                      <Button variant="secondary" size="sm" className="!w-auto" onClick={() => handleProcessDreamTeams(match.id)}>
                        ۴. پردازش تیم رویایی
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="!w-auto" onClick={() => fetchMatchWinners(match.id)}>
                      برندگان
                    </Button>
                    {footballMode === 'league' && (
                      <Button variant="outline" size="sm" className="!w-auto" onClick={() => handleOpenMatchGroups(match.id)}>
                        گروه کارت‌ها
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
              <span className="text-sm text-gray-400">صفحه {matchesPage + 1}</span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" className="!w-auto" onClick={() => setMatchesPage((p) => Math.max(0, p - 1))} disabled={matchesPage === 0}>
                  قبلی
                </Button>
                <Button variant="secondary" size="sm" className="!w-auto" onClick={() => setMatchesPage((p) => p + 1)}>
                  بعدی
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  )

  const renderMatchWinners = () => (
    <Modal
      open={winnersMatchId !== null}
      onClose={() => setWinnersMatchId(null)}
      title={`برندگان مسابقه #${winnersMatchId ?? ''}`}
    >
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {matchWinners.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            برای این مسابقه هنوز برنده‌ای ثبت نشده است.
          </p>
        ) : (
          matchWinners.map((w) => (
            <div key={w.id} className="flex items-center justify-between p-2 bg-gray-800/40 rounded text-sm">
              <span>کاربر #{w.user_id} — {w.draw_type}</span>
              <span className="text-xs text-gray-400">
                {w.prize_type === 'gold' ? '🥇 طلا' : w.prize_type === 'silver' ? '🥈 نقره' : w.prize_type}
                {w.awarded_at ? ` — ${w.awarded_at}` : ''}
              </span>
            </div>
          ))
        )}
      </div>
    </Modal>
  )

  const renderMatchModals = () => (
    <>
      <Modal
        open={editingLeague !== null}
        onClose={() => setEditingLeague(null)}
        title={`ویرایش لیگ #${editingLeague?.id ?? ''}`}
      >
        <div className="space-y-3">
          <p className="text-xs text-gray-400">
            حالت بازی ({editingLeague?.mode === 'big_match' ? 'بازی بزرگ' : 'لیگ'}) قابل تغییر نیست، چون جدول
            امتیاز و قرعه‌کشی به آن گره خورده است.
          </p>

          <div>
            <label className="block text-sm text-gray-300 mb-1">نام لیگ</label>
            <Input
              type="text"
              value={leagueForm.name}
              onChange={(e) => setLeagueForm({ ...leagueForm, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">توضیح</label>
            <Input
              type="text"
              value={leagueForm.description}
              onChange={(e) => setLeagueForm({ ...leagueForm, description: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm text-gray-300 mb-1">تاریخ شروع</label>
              <Input
                type="date"
                value={leagueForm.start_date}
                onChange={(e) => setLeagueForm({ ...leagueForm, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">تاریخ پایان</label>
              <Input
                type="date"
                value={leagueForm.end_date}
                onChange={(e) => setLeagueForm({ ...leagueForm, end_date: e.target.value })}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={leagueForm.is_active}
              onChange={(e) => setLeagueForm({ ...leagueForm, is_active: e.target.checked })}
            />
            لیگ فعال باشد
          </label>

          <Button variant="primary" className="w-full" onClick={handleSaveLeague} disabled={savingLeague}>
            {savingLeague ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
          </Button>
        </div>
      </Modal>

      <Modal
        open={statsMatchId !== null}
        onClose={() => setStatsMatchId(null)}
        title={`آمار بازیکنان مسابقه #${statsMatchId ?? ''}`}
      >
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {matchPlayers.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              بازیکنی برای این مسابقه تعریف نشده است. ابتدا در تب «فانتزی» گروه بازیکنان و در تب
              «فوتبال» گروه کارت‌های این مسابقه را تنظیم کنید.
            </p>
          ) : (
            matchPlayers.map((p) => {
              const row = playerStats[p.id] || {}
              const update = (patch: Partial<MatchPlayerStat>) =>
                setPlayerStats((prev) => ({
                  ...prev,
                  [p.id]: { player_id: p.id, position: p.position, ...prev[p.id], ...patch },
                }))
              return (
                <div key={p.id} className="p-2 bg-gray-800/40 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{p.name}</span>
                    <span className="text-xs text-gray-400">{p.position}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="number"
                      placeholder="گل"
                      value={row.goals ?? 0}
                      onChange={(e) => update({ goals: parseInt(e.target.value) || 0 })}
                    />
                    <Input
                      type="number"
                      placeholder="پاس گل"
                      value={row.assists ?? 0}
                      onChange={(e) => update({ assists: parseInt(e.target.value) || 0 })}
                    />
                    <Input
                      type="number"
                      placeholder="کارت زرد"
                      value={row.yellow_cards ?? 0}
                      onChange={(e) => update({ yellow_cards: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-300">
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={row.clean_sheet || false}
                        onChange={(e) => update({ clean_sheet: e.target.checked })}
                      />
                      کلین‌شیت
                    </label>
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={row.played_full_time ?? true}
                        onChange={(e) => update({ played_full_time: e.target.checked })}
                      />
                      بازی کامل
                    </label>
                  </div>
                </div>
              )
            })
          )}
          <Button
            variant="primary"
            className="w-full"
            onClick={handleSavePlayerStats}
            disabled={matchPlayers.length === 0 || savingStats}
          >
            {savingStats ? 'در حال ذخیره...' : 'ذخیره آمار و امتیازدهی تیم‌های رویایی'}
          </Button>
        </div>
      </Modal>
    </>
  )

  const renderFantasy = () => (
    <div className="space-y-6">
      {/* Players Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">🏆 بازیکنان فانتزی ({fantasyPlayers.length})</h3>
          <Button variant="primary" onClick={() => { setEditingPlayer(null); setNewPlayer({ name: '', position: 'fwd', team: '', photo_url: '' }); setPhotoPreview(null); setShowPlayerModal(true); }}>➕ بازیکن جدید</Button>
        </div>
        <Card>
          {fantasyPlayers.length === 0 ? (
            <div className="text-center py-6 text-gray-400">هیچ بازیکنی یافت نشد</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700">
                    <th className="pb-2">ID</th>
                    <th className="pb-2">نام</th>
                    <th className="pb-2">پوزیشن</th>
                    <th className="pb-2">تیم</th>
                    <th className="pb-2">عکس</th>
                    <th className="pb-2">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {fantasyPlayers.map((player) => (
                    <tr key={player.id} className="border-b border-gray-800">
                      <td className="py-2 text-gray-400">#{player.id}</td>
                      <td className="py-2 font-medium">{player.name}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          player.position === 'gk' ? 'bg-yellow-900/30 text-yellow-300' :
                          player.position === 'def' ? 'bg-red-900/30 text-red-300' :
                          player.position === 'mid' ? 'bg-blue-900/30 text-blue-300' :
                          'bg-green-900/30 text-green-300'
                        }`}>
                          {player.position}
                        </span>
                      </td>
                      <td className="py-2">{player.team}</td>
                      <td className="py-2">
                        {player.photo_url && <img src={player.photo_url} alt={player.name} className="w-10 h-12 rounded object-cover object-top" />}
                      </td>
                      <td className="py-2">
                        <div className="flex gap-1">
                          <Button variant="secondary" size="sm" onClick={() => { setEditingPlayer(player); setNewPlayer({ name: player.name, position: player.position, team: player.team, photo_url: player.photo_url }); setPhotoPreview(player.photo_url || null); setShowPlayerModal(true); }}>✏️</Button>
                          <Button variant="secondary" size="sm" onClick={() => handleDeletePlayer(player.id)}>🗑️</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Player Groups Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">📁 گروه‌های بازیکنان ({fantasyGroups.length})</h3>
          <Button variant="primary" onClick={() => { setEditingGroup(null); setNewGroup({ title: '', max_cards: '' }); setShowGroupModal(true); }}>➕ گروه جدید</Button>
        </div>
        <Card>
          {fantasyGroups.length === 0 ? (
            <div className="text-center py-6 text-gray-400">هیچ گروهی یافت نشد</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700">
                    <th className="pb-2">ID</th>
                    <th className="pb-2">عنوان</th>
                    <th className="pb-2">ظرفیت</th>
                    <th className="pb-2">وضعیت</th>
                    <th className="pb-2">تاریخ</th>
                    <th className="pb-2">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {fantasyGroups.map((group) => (
                    <tr key={group.id} className="border-b border-gray-800">
                      <td className="py-2 text-gray-400">#{group.id}</td>
                      <td className="py-2 font-medium">{group.title}</td>
                      <td className="py-2 text-gray-400">{group.max_cards > 0 ? group.max_cards : 'نامحدود'}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          group.is_active ? 'bg-green-900/30 text-green-300' : 'bg-gray-900/30 text-gray-300'
                        }`}>
                          {group.is_active ? '✅ فعال' : '❌ غیرفعال'}
                        </span>
                      </td>
                      <td className="py-2 text-gray-400">{formatDate(group.created_at)}</td>
                      <td className="py-2">
                        <div className="flex gap-1">
                          <Button variant="secondary" size="sm" onClick={() => handleOpenGroupPlayers(group.id)}>👥 بازیکنان</Button>
                          <Button variant="secondary" size="sm" onClick={() => { setEditingGroup(group); setNewGroup({ title: group.title, max_cards: group.max_cards.toString() }); setShowGroupModal(true); }}>✏️</Button>
                          <Button variant="secondary" size="sm" onClick={() => handleDeleteGroup(group.id)}>🗑️</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  )

  const renderTransfers = () => (
    <div>
      <div className="flex gap-2 mb-4">
        <select
          value={transfersFilter}
          onChange={(e) => { setTransfersFilter(e.target.value as any); setTransfersPage(0); }}
          className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm"
        >
          <option value="all">همه</option>
          <option value="pending">⏳ در انتظار</option>
          <option value="approved">✅ تایید شده</option>
          <option value="rejected">❌ رد شده</option>
        </select>
      </div>
      <Card>
        {transfers.length === 0 ? (
          <div className="text-center py-6 text-gray-400">هیچ تراکنشی یافت نشد</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700">
                    <th className="pb-2">ID</th>
                    <th className="pb-2">از کاربر</th>
                    <th className="pb-2">مقصد</th>
                    <th className="pb-2">نوع کوین</th>
                    <th className="pb-2">مقدار</th>
                    <th className="pb-2">وضعیت</th>
                    <th className="pb-2">تاریخ</th>
                    <th className="pb-2">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map((t) => (
                    <tr key={t.id} className="border-b border-gray-800">
                      <td className="py-2 text-gray-400">#{t.id}</td>
                      <td className="py-2">#{t.from_user_id} - {t.from_user?.first_name || ''}</td>
                      <td className="py-2">
                        {t.to_user_id ? (
                          `#${t.to_user_id}`
                        ) : t.wallet_address ? (
                          <span className="font-mono text-xs break-all" dir="ltr">{t.wallet_address}</span>
                        ) : t.card_number ? (
                          `کارت ****${t.card_number.slice(-4)}`
                        ) : (
                          'مدیر'
                        )}
                      </td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          t.coin_type === 'bronze' ? 'bg-amber-900/30 text-amber-300' :
                          t.coin_type === 'silver' ? 'bg-gray-900/30 text-gray-300' :
                          t.coin_type === 'gold' ? 'bg-yellow-900/30 text-yellow-300' :
                          'bg-cyan-900/30 text-cyan-300'
                        }`}>
                          {t.coin_type}
                        </span>
                      </td>
                      <td className="py-2 font-bold">{formatNumber(t.amount)}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          t.status === 'pending' ? 'bg-yellow-900/30 text-yellow-300' :
                          t.status === 'approved' ? 'bg-green-900/30 text-green-300' :
                          t.status === 'rejected' ? 'bg-red-900/30 text-red-300' :
                          'bg-blue-900/30 text-blue-300'
                        }`}>
                          {t.status === 'pending' ? '⏳ در انتظار' :
                           t.status === 'approved' ? '✅ تایید' :
                           t.status === 'rejected' ? '❌ رد' : '✅ تکمیل'}
                        </span>
                      </td>
                      <td className="py-2 text-gray-400">{formatDate(t.created_at)}</td>
                      <td className="py-2">
                        {t.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button variant="primary" size="sm" onClick={() => handleProcessTransfer(t.id, true)}>✅</Button>
                            <Button variant="secondary" size="sm" onClick={() => handleProcessTransfer(t.id, false)}>❌</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
              <span className="text-sm text-gray-400">صفحه {transfersPage + 1}</span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => setTransfersPage(p => Math.max(0, p - 1))} disabled={transfersPage === 0}>قبلی</Button>
                <Button variant="secondary" size="sm" onClick={() => setTransfersPage(p => p + 1)}>بعدی</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  )

  const renderTickets = () => (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="font-bold">🎧 چت پشتیبانی</h3>
        <p className="text-xs text-gray-400">یک چت برای هر کاربر · پیام‌های قدیمی‌تر از ۷ روز پاک می‌شوند</p>
      </div>

      {activeTicket ? (
        <Card>
          <div className="mb-3">
            <button type="button" className="text-xs text-gray-400 mb-1" onClick={() => setActiveTicket(null)}>
              → بازگشت به لیست
            </button>
            <h4 className="font-bold truncate">{activeTicket.subject}</h4>
            <p className="text-xs text-gray-400">
              #{activeTicket.id} · کاربر {activeTicket.user?.first_name || ''} @{activeTicket.user?.username || activeTicket.user_id}
            </p>
          </div>

          <div className="space-y-2 max-h-[50vh] overflow-y-auto mb-3 p-2 bg-gray-900/40 rounded-xl">
            {(activeTicket.messages || []).length === 0 ? (
              <div className="text-center text-gray-500 text-sm py-4">هنوز پیامی نیست</div>
            ) : (
              (activeTicket.messages || []).map((m) => (
                <div
                  key={m.id}
                  className={`p-2 rounded-xl text-sm max-w-[85%] ${
                    m.sender === 'admin'
                      ? 'bg-primary/30 mr-auto'
                      : 'bg-gray-800/60 ml-auto'
                  }`}
                >
                  <div className="text-[10px] text-gray-400 mb-1">
                    {m.sender === 'admin' ? 'پشتیبانی' : 'کاربر'} · {formatDate(m.created_at)}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                </div>
              ))
            )}
          </div>

          <div className="flex gap-2">
            <textarea
              className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-2 text-sm min-h-[64px]"
              placeholder="پاسخ..."
              value={ticketReply}
              onChange={(e) => setTicketReply(e.target.value)}
              disabled={ticketBusy}
            />
            <Button variant="primary" onClick={handleTicketReply} disabled={ticketBusy || !ticketReply.trim()}>
              ارسال
            </Button>
          </div>
        </Card>
      ) : (
        <Card>
          {tickets.length === 0 ? (
            <div className="text-center py-6 text-gray-400">چتی یافت نشد</div>
          ) : (
            <div className="space-y-2">
              {tickets.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openTicketChat(t.id)}
                  className="w-full text-right p-3 bg-gray-800/30 rounded-xl hover:bg-gray-800/50"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-medium truncate">{t.subject}</span>
                    <span className="text-xs shrink-0 text-gray-400">{formatDate(t.last_message_at)}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    #{t.id} · {t.user?.first_name || ''} @{t.user?.username || t.user_id}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )

  const renderMessages = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold">💬 پیام‌های ادمین</h3>
        <Button variant="primary" onClick={() => setShowMessageModal(true)}>➕ پیام جدید</Button>
      </div>
      <Card>
        {messages.length === 0 ? (
          <div className="text-center py-6 text-gray-400">هیچ پیامی یافت نشد</div>
        ) : (
          <>
            <div className="space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className="p-3 bg-gray-800/30 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">👤 #{msg.user_id}</span>
                    <span className="text-xs text-gray-400">{formatDate(msg.sent_at)}</span>
                  </div>
                  <div className="text-gray-300">{msg.text}</div>
                  <div className="flex items-center gap-2 mt-2">
                    {msg.read ? (
                      <span className="text-xs text-green-400">✅ خوانده شده</span>
                    ) : (
                      <span className="text-xs text-yellow-400">📩 خوانده نشده</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
              <span className="text-sm text-gray-400">صفحه {messagesPage + 1}</span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => setMessagesPage(p => Math.max(0, p - 1))} disabled={messagesPage === 0}>قبلی</Button>
                <Button variant="secondary" size="sm" onClick={() => setMessagesPage(p => p + 1)}>بعدی</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  )

  const handleDownloadBackup = async (kind: 'full' | 'database') => {
    setBackupDownloading(true)
    setBackupMessage(null)
    setError(null)
    try {
      const { blob, filename } =
        kind === 'full' ? await adminApi.downloadFullBackup() : await adminApi.downloadDatabaseBackup()
      if (blob.type && blob.type.includes('json')) {
        const text = await blob.text()
        try {
          const parsed = JSON.parse(text) as { message?: string }
          throw new Error(parsed.message || 'دانلود بکاپ ناموفق بود')
        } catch (e) {
          if (e instanceof SyntaxError) throw new Error('دانلود بکاپ ناموفق بود')
          throw e
        }
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      setBackupMessage(`دانلود شروع شد: ${filename}`)
    } catch (err: any) {
      let msg = 'خطا در دانلود بکاپ'
      const data = err?.response?.data
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text()) as { message?: string }
          if (parsed.message) msg = parsed.message
        } catch { /* ignore */ }
      } else if (err?.response?.data?.message) {
        msg = err.response.data.message
      } else if (err?.message) {
        msg = err.message
      }
      setError(msg)
    } finally {
      setBackupDownloading(false)
    }
  }

  const toggleAdminPerm = (perm: string) => {
    setAdminForm((prev) => {
      const has = prev.permissions.includes(perm)
      return {
        ...prev,
        permissions: has ? prev.permissions.filter((p) => p !== perm) : [...prev.permissions, perm],
      }
    })
  }

  const openCreateAdmin = () => {
    setEditingAdmin(null)
    setAdminForm({ username: '', password: '', permissions: [] })
  }

  const openEditAdmin = (admin: AdminAccountInfo) => {
    setEditingAdmin(admin)
    setAdminForm({
      username: admin.username,
      password: '',
      permissions: [...admin.permissions],
    })
  }

  const handleSaveAdmin = async () => {
    if (!adminForm.username.trim()) {
      setError('نام کاربری لازم است')
      return
    }
    if (!editingAdmin && adminForm.password.trim().length < 6) {
      setError('رمز حداقل ۶ کاراکتر باشد')
      return
    }
    if (editingAdmin && adminForm.password.trim() && adminForm.password.trim().length < 6) {
      setError('رمز حداقل ۶ کاراکتر باشد')
      return
    }
    setSavingAdmin(true)
    setError(null)
    try {
      if (editingAdmin) {
        await adminApi.updateAdmin(editingAdmin.id, {
          username: adminForm.username.trim(),
          permissions: adminForm.permissions,
          ...(adminForm.password.trim() ? { password: adminForm.password.trim() } : {}),
        })
      } else {
        await adminApi.createAdmin({
          username: adminForm.username.trim(),
          password: adminForm.password.trim(),
          permissions: adminForm.permissions,
        })
      }
      setEditingAdmin(null)
      setAdminForm({ username: '', password: '', permissions: [] })
      const res = await adminApi.listAdmins()
      setManagedAdmins(res.admins || [])
      setPermissionLabels(res.permission_labels || {})
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ذخیره ادمین')
    } finally {
      setSavingAdmin(false)
    }
  }

  const handleToggleAdminActive = async (admin: AdminAccountInfo) => {
    try {
      await adminApi.updateAdmin(admin.id, { is_active: !admin.is_active })
      const res = await adminApi.listAdmins()
      setManagedAdmins(res.admins || [])
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در تغییر وضعیت')
    }
  }

  const handleDeleteAdmin = async (admin: AdminAccountInfo) => {
    if (!confirm(`ادمین «${admin.username}» حذف شود؟`)) return
    try {
      await adminApi.deleteAdmin(admin.id)
      setManagedAdmins((prev) => prev.filter((a) => a.id !== admin.id))
      if (editingAdmin?.id === admin.id) {
        setEditingAdmin(null)
        setAdminForm({ username: '', password: '', permissions: [] })
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف ادمین')
    }
  }

  const renderAdmins = () => (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h3 className="font-bold text-lg">مدیریت ادمین‌ها</h3>
            <p className="text-sm text-gray-400 mt-1">فقط سوپر ادمین می‌تواند ادمین بسازد و دسترسی‌ها را تنظیم کند.</p>
          </div>
          <Button variant="secondary" size="sm" className="!w-auto" onClick={openCreateAdmin}>
            ➕ ادمین جدید
          </Button>
        </div>

        <div className="space-y-3 rounded-xl bg-gray-800/40 p-3">
          <div className="text-sm font-medium">{editingAdmin ? `ویرایش · ${editingAdmin.username}` : 'ساخت ادمین جدید'}</div>
          <Input
            value={adminForm.username}
            onChange={(e) => setAdminForm((p) => ({ ...p, username: e.target.value }))}
            placeholder="نام کاربری"
          />
          <Input
            type="password"
            value={adminForm.password}
            onChange={(e) => setAdminForm((p) => ({ ...p, password: e.target.value }))}
            placeholder={editingAdmin ? 'رمز جدید (اختیاری)' : 'رمز (حداقل ۶ کاراکتر)'}
          />
          <div>
            <div className="text-xs text-gray-400 mb-2">دسترسی بخش‌ها</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ASSIGNABLE_PERMS.map((perm) => {
                const checked = adminForm.permissions.includes(perm)
                const label = permissionLabels[perm] || TABS.find((t) => t.id === perm)?.label || perm
                return (
                  <label key={perm} className="flex items-center gap-2 text-sm bg-gray-900/50 rounded-lg px-2 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAdminPerm(perm)}
                    />
                    <span>{label}</span>
                  </label>
                )
              })}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => void handleSaveAdmin()} disabled={savingAdmin}>
              {savingAdmin ? 'در حال ذخیره…' : editingAdmin ? 'ذخیره تغییرات' : 'ایجاد ادمین'}
            </Button>
            {editingAdmin && (
              <Button variant="secondary" onClick={openCreateAdmin}>انصراف</Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-4 space-y-2">
        <h4 className="font-bold">لیست ادمین‌ها · {managedAdmins.length}</h4>
        {managedAdmins.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">هنوز ادمینی ساخته نشده</p>
        ) : (
          managedAdmins.map((admin) => (
            <div key={admin.id} className="rounded-xl bg-gray-800/40 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-medium">{admin.username}</div>
                  <div className="text-xs text-gray-400">
                    {admin.is_active ? 'فعال' : 'غیرفعال'} · {admin.permissions.length} دسترسی
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="secondary" size="sm" className="!w-auto !px-2" onClick={() => openEditAdmin(admin)}>✏️</Button>
                  <Button variant="secondary" size="sm" className="!w-auto !px-2" onClick={() => void handleToggleAdminActive(admin)}>
                    {admin.is_active ? 'غیرفعال' : 'فعال'}
                  </Button>
                  <Button variant="secondary" size="sm" className="!w-auto !px-2" onClick={() => void handleDeleteAdmin(admin)}>🗑</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {admin.permissions.map((p) => (
                  <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                    {permissionLabels[p] || p}
                  </span>
                ))}
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  )

  const renderBackup = () => (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div>
          <h3 className="font-bold text-lg">بکاپ کامل (پیشنهادی)</h3>
          <p className="text-sm text-gray-400 mt-1">
            دیتابیس + همهٔ عکس‌ها و فایل‌های آپلود در یک فایل. اگر دیتابیس پاک شود، با این بکاپ داده‌ها و تصاویر قابل برگشت‌اند.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => handleDownloadBackup('full')}
          disabled={backupDownloading}
        >
          {backupDownloading ? 'در حال ساخت بکاپ…' : '⬇️ دانلود بکاپ کامل'}
        </Button>
        {backupDownloading && (
          <p className="text-xs text-amber-300">ممکن است چند ده ثانیه طول بکشد؛ صفحه را نبندید.</p>
        )}
        {backupMessage && (
          <p className="text-sm text-green-300">{backupMessage}</p>
        )}
      </Card>
      <Card className="p-4 space-y-3">
        <div>
          <h3 className="font-bold">فقط دیتابیس</h3>
          <p className="text-sm text-gray-400 mt-1">فایل سبک‌تر؛ عکس‌های آپلود داخلش نیست.</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => handleDownloadBackup('database')}
          disabled={backupDownloading}
        >
          دانلود فقط SQL
        </Button>
      </Card>
    </div>
  )

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard()
      case 'codes': return renderCodes()
      case 'gifts': return renderGifts()
      case 'scores': return renderScores()
      case 'monthly': return renderMonthly()
      case 'users': return renderUsers()
      case 'guess-number': return renderGuessNumber()
      case 'morse': return renderMorse()
      case 'football': return (
        <>
          {renderFootball()}
          {renderMatchWinners()}
          {renderMatchModals()}
        </>
      )
      case 'fantasy': return renderFantasy()
      case 'horse': return <AdminHorsePanel />
      case 'transfers': return renderTransfers()
      case 'coin-rates': return <AdminCoinRatesPanel />
      case 'tickets': return renderTickets()
      case 'messages': return renderMessages()
      case 'backup': return renderBackup()
      case 'admins': return renderAdmins()
    }
  }

  return (
    <div className="mini-app-container pb-20 min-h-screen">
      <div className="p-4 pt-16">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔐</span>
            <div>
              <h1 className="text-xl font-bold">پنل ادمین</h1>
              {currentAdmin && (
                <p className="text-xs text-gray-400">
                  {currentAdmin.username}
                  {currentAdmin.is_super ? ' · سوپر ادمین' : ''}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-xs px-3 py-1 bg-gray-800 text-gray-400 rounded-lg hover:bg-gray-700"
          >
            خروج
          </button>
        </div>

        <div className="grid grid-cols-5 gap-1 mb-4 bg-gray-800/50 rounded-xl p-1">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-1 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-primary text-white'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 text-red-300 rounded-lg text-sm text-center">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-gray-400">⏳ در حال بارگذاری...</div>
        ) : (
          renderContent()
        )}

        {/* Create Code Modal */}
        <Modal open={showCodeModal} onClose={() => setShowCodeModal(false)} title="ایجاد کد پاداش">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">شناسه کاربر (اختیاری)</label>
              <Input type="number" placeholder="خالی = همه" value={newCode.user_id} onChange={(e) => setNewCode({...newCode, user_id: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">نوع پاداش</label>
              <select value={newCode.award_type} onChange={(e) => setNewCode({...newCode, award_type: e.target.value as 'daily' | 'monthly'})} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg">
                <option value="daily">روزانه</option>
                <option value="monthly">ماهانه</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">ساعت اعتبار (۰ = بدون انقضا)</label>
              <Input type="number" value={newCode.valid_hours} onChange={(e) => setNewCode({...newCode, valid_hours: parseInt(e.target.value) || 0})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">کد سفارشی (اختیاری)</label>
              <Input placeholder="خالی = رندوم" value={newCode.code} onChange={(e) => setNewCode({...newCode, code: e.target.value})} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => setShowCodeModal(false)}>انصراف</Button>
              <Button variant="primary" className="flex-1" onClick={handleCreateCode}>ایجاد</Button>
            </div>
          </div>
        </Modal>

        {/* Create Match Modal */}
        <Modal
          open={showMatchModal}
          onClose={() => { setShowMatchModal(false); setShowInlineClub(null) }}
          title={`مسابقه جدید · ${newMatch.mode === 'big_match' ? 'بازی بزرگ' : 'لیگ روزانه'}`}
        >
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-800/60 px-3 py-2 text-sm text-gray-300">
              این مسابقه زیر{' '}
              <strong className="text-white">
                {newMatch.mode === 'big_match' ? 'بازی بزرگ' : 'لیگ روزانه'}
              </strong>{' '}
              ساخته می‌شود. تیم را از کاتالوگ انتخاب کن یا سریع بساز.
            </div>
            {(['home', 'away'] as const).map((side) => {
              const selectedId = side === 'home' ? newMatch.home_team_id : newMatch.away_team_id
              const selected = clubs.find((c) => c.id === selectedId)
              const label = side === 'home' ? 'تیم میزبان' : 'تیم مهمان'
              return (
                <div key={side}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-300">{label}</label>
                    <button
                      type="button"
                      className="text-xs text-blue-300 hover:text-blue-200"
                      onClick={() => { setShowInlineClub(side); openClubModal() }}
                    >
                      + ساخت باشگاه
                    </button>
                  </div>
                  {activeClubs.length > 0 ? (
                    <select
                      className="w-full rounded-lg bg-gray-800 border border-gray-700 text-white px-3 py-2 text-sm"
                      value={selectedId || ''}
                      onChange={(e) => {
                        const id = Number(e.target.value) || 0
                        const club = clubs.find((c) => c.id === id)
                        if (side === 'home') {
                          setNewMatch({ ...newMatch, home_team_id: id, home_team: club?.name || '' })
                        } else {
                          setNewMatch({ ...newMatch, away_team_id: id, away_team: club?.name || '' })
                        }
                      }}
                    >
                      <option value="">انتخاب از کاتالوگ…</option>
                      {activeClubs.map((club) => (
                        <option key={club.id} value={club.id}>{club.name}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={side === 'home' ? newMatch.home_team : newMatch.away_team}
                      onChange={(e) =>
                        side === 'home'
                          ? setNewMatch({ ...newMatch, home_team: e.target.value, home_team_id: 0 })
                          : setNewMatch({ ...newMatch, away_team: e.target.value, away_team_id: 0 })
                      }
                      placeholder={side === 'home' ? 'مثال: پرسپولیس' : 'مثال: استقلال'}
                    />
                  )}
                  {selected?.flag_url && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                      <img src={selected.flag_url} alt="" className="w-6 h-6 rounded object-cover" />
                      {selected.name}
                    </div>
                  )}
                </div>
              )
            })}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">زمان شروع</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={kickoffDate}
                  onChange={(e) => setKickoff(e.target.value, kickoffTime || '18:00')}
                />
                <Input
                  type="time"
                  step={300}
                  value={kickoffTime}
                  onChange={(e) => setKickoff(kickoffDate, e.target.value)}
                />
              </div>
              {newMatch.start_time && (
                <p className="text-xs text-gray-400 mt-2">
                  شروع مسابقه: <span className="text-white">{formatDate(new Date(newMatch.start_time).toISOString())}</span>
                  {' '}(به وقت محلی مرورگر)
                </p>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => { setShowMatchModal(false); setShowInlineClub(null) }}>انصراف</Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleCreateMatch}
                disabled={(!newMatch.home_team_id && !newMatch.home_team) || (!newMatch.away_team_id && !newMatch.away_team) || !newMatch.start_time}
              >
                ایجاد
              </Button>
            </div>
          </div>
        </Modal>

        {/* Create/Edit Club Modal */}
        <Modal
          open={showClubModal}
          onClose={() => { setShowClubModal(false); setEditingClub(null); setShowInlineClub(null); setClubFlagPreview(null) }}
          title={editingClub ? 'ویرایش باشگاه' : 'باشگاه جدید'}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">نام باشگاه</label>
              <Input value={clubForm.name} onChange={(e) => setClubForm({ ...clubForm, name: e.target.value })} placeholder="مثال: پرسپولیس" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">پرچم / لوگو</label>
              <div className="flex items-center gap-3">
                {clubFlagPreview ? (
                  <img src={clubFlagPreview} alt="" className="w-12 h-12 rounded object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded bg-gray-700" />
                )}
                <label className="text-sm text-blue-300 cursor-pointer">
                  {clubFlagUploading ? 'در حال آپلود…' : 'انتخاب تصویر'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleClubFlagUpload} disabled={clubFlagUploading} />
                </label>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => { setShowClubModal(false); setEditingClub(null); setShowInlineClub(null) }}>انصراف</Button>
              <Button variant="primary" className="flex-1" onClick={handleSaveClub} disabled={!clubForm.name.trim()}>
                {editingClub ? 'ذخیره' : 'ایجاد'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Set Match Result Modal */}
        <Modal open={showResultModal} onClose={() => setShowResultModal(false)} title="ثبت نتیجه مسابقه">
          <div className="space-y-4">
            <div className="text-center py-3 bg-gray-800/50 rounded-xl">
              <span className="text-lg font-bold">{resultHomeTeam}</span>
              <span className="text-gray-400 mx-2">VS</span>
              <span className="text-lg font-bold">{resultAwayTeam}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">گل تیم میزبان</label>
              <Input type="number" value={resultHomeScore} onChange={(e) => setResultHomeScore(e.target.value)} placeholder="مثال: 2" min={0} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">گل تیم مهمان</label>
              <Input type="number" value={resultAwayScore} onChange={(e) => setResultAwayScore(e.target.value)} placeholder="مثال: 1" min={0} />
            </div>
            {footballMode === 'big_match' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  قرعه‌کشی بین چند نفر از پیش‌بینی‌های درست؟
                </label>
                <Input
                  type="number"
                  value={resultRaffleCount}
                  onChange={(e) => setResultRaffleCount(e.target.value)}
                  placeholder="مثال: 5"
                  min={1}
                />
                <p className="mt-1 text-xs text-gray-400">
                  همان لحظه ثبت نتیجه، بین همین تعداد از کسانی که درست گفته‌اند ۱ طلا قرعه می‌شود.
                </p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => setShowResultModal(false)}>انصراف</Button>
              <Button variant="primary" className="flex-1" onClick={handleProcessResult}>ثبت نتیجه</Button>
            </div>
          </div>
        </Modal>

        {/* Create/Edit Player Modal */}
        <Modal open={showPlayerModal} onClose={() => { setShowPlayerModal(false); setEditingPlayer(null); setNewPlayer({ name: '', position: 'fwd', team: '', photo_url: '' }); setPhotoPreview(null); }} title={editingPlayer ? 'ویرایش بازیکن' : 'ایجاد بازیکن'}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">نام</label>
              <Input value={newPlayer.name} onChange={(e) => setNewPlayer({...newPlayer, name: e.target.value})} placeholder="مثال: مسیح علیپور" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">پوزیشن</label>
              <select value={newPlayer.position} onChange={(e) => setNewPlayer({...newPlayer, position: e.target.value})} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg">
                <option value="gk">دروازه‌بان (GK)</option>
                <option value="def">دفاع (DEF)</option>
                <option value="mid">میان‌دفاع (MID)</option>
                <option value="fwd">حمله (FWD)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">تیم</label>
              <Input value={newPlayer.team} onChange={(e) => setNewPlayer({...newPlayer, team: e.target.value})} placeholder="مثال: پرسپولیس" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">عکس بازیکن (اختیاری)</label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={photoUploading}
                  className="flex-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-white hover:file:bg-gray-600"
                />
                {photoPreview && (
                  <img src={photoPreview} alt="preview" className="w-16 h-20 rounded object-cover object-top border-2 border-gray-600" />
                )}
              </div>
              {photoUploading && <span className="text-xs text-gray-400">در حال آپلود...</span>}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => { setShowPlayerModal(false); setEditingPlayer(null); setNewPlayer({ name: '', position: 'fwd', team: '', photo_url: '' }); setPhotoPreview(null); }}>انصراف</Button>
              <Button variant="primary" className="flex-1" onClick={handleCreatePlayer} disabled={!newPlayer.name || !newPlayer.team}>{editingPlayer ? 'ذخیره' : 'ایجاد'}</Button>
            </div>
          </div>
                 </Modal>

        {/* Create/Edit Group Modal */}
        <Modal open={showGroupModal} onClose={() => { setShowGroupModal(false); setEditingGroup(null); setNewGroup({ title: '', max_cards: '' }); }} title={editingGroup ? 'ویرایش گروه' : 'ایجاد گروه جدید'}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">عنوان گروه</label>
              <Input value={newGroup.title} onChange={(e) => setNewGroup({...newGroup, title: e.target.value})} placeholder="مثال: بازیکنان پر ستاره" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">ظرفیت (0 = نامحدود)</label>
              <Input type="number" value={newGroup.max_cards} onChange={(e) => setNewGroup({...newGroup, max_cards: e.target.value})} placeholder="مثال: 100" min={0} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => { setShowGroupModal(false); setEditingGroup(null); setNewGroup({ title: '', max_cards: '' }); }}>انصراف</Button>
              <Button variant="primary" className="flex-1" onClick={handleCreateGroup} disabled={!newGroup.title}>{editingGroup ? 'ذخیره' : 'ایجاد'}</Button>
            </div>
          </div>
        </Modal>

        {/* Group Players Modal */}
        <Modal open={showGroupPlayersModal} onClose={() => setShowGroupPlayersModal(false)} title={selectedGroupId ? `بازیکنان گروه #${selectedGroupId}` : 'بازیکنان گروه'}>
          <div className="space-y-4">
            <p className="text-sm text-gray-400 mb-2">
              {groupPlayers.length} بازیکن در این گروه
            </p>
            {groupPlayers.length === 0 ? (
              <div className="text-center py-4 text-gray-400">هیچ بازیکنی در این گروه نیست</div>
            ) : (
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="pb-2">نام</th>
                      <th className="pb-2">پوزیشن</th>
                      <th className="pb-2">تیم</th>
                      <th className="pb-2">عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupPlayers.map((player) => (
                      <tr key={player.id} className="border-b border-gray-800">
                        <td className="py-2">{player.name}</td>
                        <td className="py-2 text-gray-400">{player.position}</td>
                        <td className="py-2 text-gray-400">{player.team}</td>
                        <td className="py-2">
                          <Button variant="secondary" size="sm" onClick={() => handleRemovePlayerFromGroup(player.id)}>❌ حذف</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="pt-3 border-t border-gray-700">
              <label className="block text-sm font-medium text-gray-300 mb-2">اضافه کردن بازیکن به گروه</label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {fantasyPlayers.filter(p => !groupPlayers.some(gp => gp.id === p.id)).map((player) => (
                  <div key={player.id} className="flex items-center justify-between p-2 bg-gray-800/30 rounded-lg">
                    <div>
                      <span className="font-medium">{player.name}</span>
                      <span className="text-xs text-gray-400">({player.position} - {player.team})</span>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => handleAddPlayerToGroup(player.id)}>➕ افزودن</Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>

        {/* Match Groups Modal */}
        <Modal open={showMatchGroupsModal} onClose={() => setShowMatchGroupsModal(false)} title={matchGroupsMatchId ? `گروه کارت‌های بازی #${matchGroupsMatchId}` : 'گروه کارت‌های بازی'}>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              گروه‌هایی که انتخاب می‌کنید، کارت‌های آن‌ها فقط در تیم‌سازی این بازی به کاربران نمایش داده می‌شود. اگر هیچ گروهی انتخاب نشود، همهٔ بازیکنان فعال نمایش داده می‌شوند.
            </p>
            {allGroups.length === 0 ? (
              <div className="text-center py-4 text-gray-400">ابتدا در تب «فانتزی» یک گروه بسازید</div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {allGroups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => handleToggleMatchGroup(group.id)}
                    className={`w-full flex items-center justify-between p-2 rounded-lg text-right ${
                      matchGroupsSelected.includes(group.id) ? 'bg-primary/20 border border-primary' : 'bg-gray-800/30 border border-transparent'
                    }`}
                  >
                    <span className="font-medium">{group.title}</span>
                    <span className="text-xs text-gray-400">
                      {group.is_active ? 'فعال' : 'غیرفعال'} · {matchGroupsSelected.includes(group.id) ? '✅ انتخاب شده' : 'انتخاب نشده'}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => setShowMatchGroupsModal(false)}>انصراف</Button>
              <Button variant="primary" className="flex-1" onClick={handleSaveMatchGroups} disabled={savingMatchGroups}>
                {savingMatchGroups ? 'در حال ذخیره...' : `ذخیره (${matchGroupsSelected.length} گروه)`}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Send Message Modal */}
        <Modal open={showMessageModal} onClose={() => setShowMessageModal(false)} title="ارسال پیام به کاربر">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">شناسه کاربر</label>
              <Input type="number" placeholder="مثال: 123456" value={newMessage.user_id} onChange={(e) => setNewMessage({...newMessage, user_id: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">متن پیام</label>
              <textarea
                rows={4}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-primary"
                value={newMessage.text}
                onChange={(e) => setNewMessage({...newMessage, text: e.target.value})}
                placeholder="متن پیام خود را اینجا بنویسید..."
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => setShowMessageModal(false)}>انصراف</Button>
              <Button variant="primary" className="flex-1" onClick={handleSendMessage} disabled={!newMessage.user_id.trim() || !newMessage.text.trim()}>ارسال</Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  )
}

function StatCard({ icon, label, value, color }: { icon: string; label: string; value: string | number; color: string }) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-900/30 text-blue-300',
    green: 'bg-green-900/30 text-green-300',
    purple: 'bg-purple-900/30 text-purple-300',
    orange: 'bg-orange-900/30 text-orange-300',
    amber: 'bg-amber-900/30 text-amber-300',
    gray: 'bg-gray-900/30 text-gray-300',
    yellow: 'bg-yellow-900/30 text-yellow-300',
    cyan: 'bg-cyan-900/30 text-cyan-300',
  }
  return (
    <div className={`p-3 rounded-xl ${colors[color] || 'bg-gray-800/30'}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">{icon}</span>
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  )
}